/**
 * New-request screen (active tab 1 of 2). Loads picker data from
 * GET /api/meta on mount, then assembles + validates with the SAME
 * validateRequestInput the server runs (request-form-logic.ts only
 * assembles the payload; it never re-implements a validation rule).
 * Submits to POST /api/requests. No price is ever rendered here.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
// `Fragment` is imported as a VALUE (not the `<>` shorthand) because each
// equipment option renders a label AND its description as siblings, and only an
// explicit Fragment can carry the `key`.
import { Fragment } from 'preact';
import type { JSX } from 'preact';
import {
  cleanStoredText,
  allowedPayerKinds,
  DESTINATION_TEXT_GROWTH,
  DESTINATION_TEXT_MAX_LEN,
  EQUIPMENT_FORM_OPTIONS,
  sameStoredName,
  validateRequestInput,
} from '../../shared/request-schema.js';
import { isResidentId, type ApiClient } from '../api.js';
import {
  assembleRequestPayload,
  emptyRequestFormValues,
  derivePickup,
  type RequestFormValues,
} from '../request-form-logic.js';
import { myResidentsCopy, requestFormCopy, requesterBoxCopy, sharedCopy } from '../copy.js';
import { EmptyState, FormField, formatWhenParts } from '@ecmb/ui';
import type { PortalRole } from '../../shared/roles.js';

/**
 * ⛔⛔ WHAT A RESIDENT REFUSAL WAS ABOUT, so a keystroke can only clear the
 * refusals it actually answers. II-portal-#12 again, T1723 M4 security review
 * (LOW).
 *
 * ⛔ `errors.residentId` CARRIES FOUR DIFFERENT TRUTH CONDITIONS and one door
 * used to clear all of them on every character:
 *   · `'Select a resident.'` is about a BLANK box. Naming somebody answers it.
 *   · `alreadyOnList(name)` is about the exact TEXT she sent. Only text that
 *     names somebody else answers it.
 *   · `listFull`, `writesClosed`, `goneFromList` and `addResidentReadOnly` are
 *     CAPABILITY facts about the home, not about the box. No keystroke can
 *     answer one, so a keystroke must not take it off the screen and leave her
 *     believing the problem went away.
 *   · `removedOffer` is the DD10 offer, and it is about the name she sent too.
 *
 * ⛔ SO THE BOX'S "VALUE" FOR ERROR PURPOSES IS THE TEXT THE REFUSAL WAS ABOUT,
 * and the ref that records it needs a THIRD value beside "a string" and "nothing
 * recorded": this one, meaning a refusal no text can answer. A `unique symbol`
 * rather than a sentinel string, because every string is also a legitimate
 * resident name.
 */
const NOT_ABOUT_TEXT: unique symbol = Symbol('refusal-not-about-text');

/**
 * Do two Resident-box texts name the same person, by the STORE'S own rule?
 *
 * ⛔ `sameStoredName` IS THE SERVER'S COMPOSITION (`cleanStoredText`, then the
 * case fold), reused rather than approximated a fourth way, so a trailing
 * space, a doubled interior space or a case change is NOT a move. Those are
 * exactly the keystrokes that answer nothing, and the whole point of the
 * provenance check is that they must not clear a refusal.
 * ⚠️ THE BLANK-TO-BLANK ARM IS THIS FUNCTION'S OWN. `sameStoredName` answers
 * false when either side cleans to nothing, which is right for IDENTITY (two
 * invisible strings are both no resident). For THIS question, "she deleted the
 * space she had just typed" has to read as no movement at all.
 */
function sameResidentText(a: string, b: string): boolean {
  if (cleanStoredText(a) === null && cleanStoredText(b) === null) return true;
  return sameStoredName(a, b);
}

/**
 * ⚠ WHAT THIS TYPE NO LONGER CARRIES, AND WHY THE ABSENCE IS THE POINT
 * (T1868 M6). It used to declare the roster's optional `nextOfKinName` /
 * `nextOfKinPhone` so the form could prefill them. LCC asked for next of kin to
 * come off the request, so the pair is gone from the form, from the roster, from
 * the validator and from every write path — and the number Helen rings is now
 * the HOME's own `fallback_phone`, one organisation-level value she sets on the
 * kiosk, never a third party's contact riding on a resident row.
 *
 * ⛔ SO THE READ IS NOT MERELY UNUSED, IT IS FORBIDDEN. `listActiveResidentsForHome`
 * stops selecting the columns and `getMeta` therefore stops serving them; a type
 * that still declared them would let a future edit re-introduce the read without
 * anything failing. `room` is the one prefilled field that survives.
 */
type MetaResident = {
  id: number;
  displayName: string;
  room: string;
};
type MetaDestination = { id: number; name: string };
/**
 * ⭐⭐ T2075 M3 (plan D12) — ONE ROW OF THE PICKER, exactly as `GET /api/meta`
 * serves it (`core/staff-repo.ts` `listPickableStaff`): already scoped on the
 * SESSION's role and group by the server, already ordered recently-used first
 * then A-Z. The form renders the list in wire order and never re-sorts or
 * re-filters a `group` account's list; a reception or admin account narrows
 * the home's list by `groupLabel` in the browser, and the server re-checks
 * whatever id is posted (`insertRequest`), so this list is cosmetic.
 */
type MetaStaff = { staffId: number; displayName: string; groupLabel: string; lastRequestedAt: string | null };
type MetaResponse = {
  /**
   * ⭐⭐ T1868 M5 (P0-4) — `pickupOffsetMinutes` JOINS THE HOME.
   *
   * ⛔ OPTIONAL ON THE WIRE AND NULL IS A REAL VALUE, for two different reasons
   * that both mean the same thing here: a portal deployed before v12 does not
   * send the key at all, and a fixture or mirror that predates the column sends
   * null. Both mean NO PREFILL — the form offers nothing rather than inventing
   * the schema's own `DEFAULT 60`, because a number no home ever stated is
   * precisely the invented "Pickup time" T632 retired.
   * ⚠️ IT IS NOT A CAPABILITY FLAG: it is the home's stated fact, and the form
   * derives from it rather than deciding anything on it.
   */
  home: { id: number; name: string; pickupOffsetMinutes?: number | null } | null;
  /**
   * ⭐ Board 2638 (plan D3) — THE SERVER'S CLOCK at response time, ISO. The form
   * judges "in the future" and "too soon" against an estimate built from it,
   * never against the device's clock. Optional: absent or unparseable reads as
   * the epoch, the most lenient estimate, and the server re-checks every bound.
   */
  serverNow?: string;
  residents: MetaResident[];
  destinations: MetaDestination[];
  /**
   * ⭐ T2075 M3 — THE PICKER'S NAMES AND THE HOME'S GROUP LABELS (M2 put both on
   * the wire). ⚠️ OPTIONAL ON THE TYPE, AND ABSENT READS AS EMPTY: a mirror or a
   * fixture that predates M2 sends neither, and the empty list is the CLOSED
   * posture (the `requesterEmpty` state, and the validator's required key keeps
   * refusing the send) - an absent key can never widen who may be named.
   */
  staff?: MetaStaff[];
  groups?: string[];
  payerKinds: string[];
  /**
   * ⛔ A SERVER-DECIDED CAPABILITY, NEVER INFERRED HERE (T1131). The variable
   * behind it is not readable from the browser and must not be guessed at: the
   * server publishes the result of the SAME predicate its routes enforce.
   *
   * ⚠️ OPTIONAL, AND AN ABSENT KEY MEANS `false` — the CLOSED posture. Defaulting
   * true would reproduce the exact defect this closed: a form promising a
   * resident-creation path the server refuses. False can never open a write,
   * because the server independently re-checks it
   * (measured in request-routes.spec.ts).
   *
   * ⚠️ THERE WERE TWO OF THESE AND `syntheticPhones` IS GONE (T1868 M6). It said
   * whether a NEXT-OF-KIN number had to sit in the synthetic drama range, and
   * there is no phone on this form any more, so `getMeta` stopped publishing it.
   * ⛔ `residentsWritable` IS A DIFFERENT QUESTION AND IS UNCHANGED: it reads
   * `PORTAL_RESIDENT_WRITES_ACK_ARTICLE9`, the Article 9 write acknowledgement,
   * which has never been derived from the delivery posture and must not start.
   */
  residentsWritable?: boolean;
  /**
   * ⭐ board 2732 M4/M5 (DD7) — CAN THIS SESSION ADD A MISSING NAME? Decided on
   * the server (`getMeta`), the `residentsWritable` precedent: a `group` account
   * with a label, or reception / admin at a home with an active group. Absent
   * means `false`, the closed posture: the form offers no `Add`.
   */
  staffAddable?: boolean;
};

export type RequestFormProps = {
  api: ApiClient;
  /** Locks the device (POST /api/auth/lock) and returns to the picker. */
  onLock: () => void;
  /**
   * ⭐ II-portal-#27 (T1723 M4) — fires `true` while the "Request sent" panel is
   * what this tab is showing, `false` while the form is. The tab bar lives in
   * `app.tsx` and cannot see this screen's `success` state, so the tab kept
   * announcing itself as `New request` pressed on a screen that is no longer
   * the new-request form.
   * ⚠️ OPTIONAL, so every existing caller and every spec that renders this
   * screen on its own keeps working unchanged.
   */
  onSentChange?: (sent: boolean) => void;
  /**
   * ⭐⭐ T2075 M3 (plan D12) — THE SESSION'S ROLE, from `app.tsx`
   * (`auth.nurse.role`, published by `GET /api/auth/state` since M2). It decides
   * the SHAPE of the picker only: a `group` account gets one name select over
   * its own group's list; `reception` and `admin` get a group select first and
   * the name select narrowed by it. It decides nothing about WHO may be named -
   * the server scopes the list it serves and re-checks the id it receives.
   * ⚠️ OPTIONAL, DEFAULTING TO `group`, the narrowest shape: every existing
   * caller and every spec that renders this screen on its own keeps working,
   * and a caller that forgets the prop gets the one-select form, never the
   * two-select one.
   */
  role?: PortalRole;
  /**
   * ⭐ board 2732 M5 — a `group` account's own group label (the auth state's),
   * for the staged-add hint. Read only for its words: with an empty roster
   * `meta.staff` has no row to take the group from. The server decides the
   * group whatever this says (`POST /api/staff`).
   */
  groupLabel?: string | null;
  /**
   * ⭐ Board 2706 (plan D4) — the unsaved form a lock took off the screen, handed back by the
   * shell on a PIN unlock by the same account. Read ONCE, at mount.
   */
  initialDraft?: RequestFormDraft;
  /**
   * ⭐ Board 2706 (plan D4) — this form's unsaved work, reported on every change to it: the draft,
   * or `null` when the form is pristine or the "Request sent" panel is up. It is the ONE definition
   * of unsaved work, used by the shell both to keep a draft across a lock and to decide that a
   * reload onto a new build loses nothing.
   */
  onDraftChange?: (draft: RequestFormDraft | null) => void;
  /**
   * ⭐⭐ Board 2706 rework (R1) — THE SEND'S PHASE, reported from inside `sendRequest` itself, so
   * the report survives the lock unmounting this form mid-send (the async chain keeps running and
   * the POST still lands): `sending` with the draft as sent, then `sent` with the panel's facts or
   * `failed`. The shell holds the outcome, so a request that went while the screen was locked
   * comes back as its "Request sent" panel, never as a draft that could be sent a second time.
   */
  onSendPhase?: (phase: SendPhase) => void;
  /** ⭐ Board 2706 rework (R1) — this mount shows a request whose send is still in flight: Send
   *  stays disabled until the shell remounts the form onto the outcome. READ AT MOUNT ONLY. */
  sendPending?: boolean;
  /** ⭐ Board 2706 rework (R1) — the "Request sent" panel a send that settled while the screen was
   *  locked left behind; this mount shows it instead of the form. Read at mount. */
  initialSent?: RequestSentFacts;
  /** ⭐ Board 2706 rework (R1, round 2) — the signed-in account, so a send started by one account
   *  never makes a write once another is signed in (`stillMine`). */
  accountId?: number;
};

/** The facts the "Request sent" panel shows (T1101, T1683 M3). */
export type RequestSentFacts = {
  residentName: string;
  destinationName: string;
  requestId: number;
  /** ⭐ T1683 M3 (A1). `null` when the server did not send one - an older
   *  build, or a stub. The panel then omits the reference line entirely
   *  rather than rendering a blank label. */
  jobRef: string | null;
  alertFailed: boolean;
};

/** Board 2706 rework (R1) — a send's phase; `of` is the very draft the `sending` report carried,
 *  so the shell can tell this send's outcome from a stale one. */
export type SendPhase =
  // `replaces`: the same send has moved on (a resident was created or put back), so its held copy
  // becomes this draft, but ONLY if the held copy is still the one this send reported.
  | { kind: 'sending'; draft: RequestFormDraft; replaces?: RequestFormDraft }
  | { kind: 'sent'; of: RequestFormDraft; sent: RequestSentFacts }
  | { kind: 'failed'; of: RequestFormDraft };

/** ⭐ Board 2706 (plan D4) — what the shell holds across a lock: everything the nurse typed
 *  (`values`, the Resident box) and who authored the prefilled field, so a restored room keeps
 *  the provenance rule `applyResidentMatch` reads. */
export type RequestFormDraft = {
  values: RequestFormValues;
  residentQuery: string;
  fieldAuthor: Record<string, string>;
};

/** Board 2706 — pristine is every value at its empty default and an empty Resident box. */
function isPristineForm(values: RequestFormValues, residentQuery: string): boolean {
  if (residentQuery !== '') return false;
  const empty = emptyRequestFormValues();
  return (Object.keys(empty) as (keyof RequestFormValues)[]).every(
    (k) => JSON.stringify(values[k]) === JSON.stringify(empty[k]),
  );
}

/**
 * ⭐ `ADR4-D10` (T1033 M8) — validation key → the control the nurse must fix,
 * **IN THE ORDER THE FIELDS APPEAR ON SCREEN**.
 *
 * ⛔ THE ORDER IS THE WHOLE POINT AND IT CANNOT BE DERIVED FROM THE ERRORS
 * OBJECT. `Object.keys(errors)` is insertion-ordered by whatever the validator
 * checked first, which has no relation to layout — so "the first error" taken
 * from there can be several fields BELOW one the nurse would meet earlier.
 * This list is read top-to-bottom and is the source of "first".
 *
 * ⚠️ DELIBERATELY PARTIAL, and that is safe by construction rather than by
 * luck. The equipment checkbox group, the payer radios and the `journey`
 * cross-field key have no single control that is meaningfully "the" target, so
 * they are omitted; `focusFirstError` treats a miss as "keep looking" and
 * simply moves on. ⛔ A key added to the validator does NOT need adding here to
 * stay correct — it only misses the focus courtesy. That direction is the safe
 * one: the alternative (guessing a target) would send her to the wrong field.
 *
 * ⭐⭐ T2075 M3 — THE REQUESTER IS ROW 0, because the picker is the FIRST control
 * on the form (plan D12: "first and always"). This list IS the form's own
 * order, so the requester sits above the resident here exactly as it does on
 * screen; the e2e discriminator in `portal-form-errors.spec.ts` (focus reaches
 * the first INVALID control, not the first control) fills it before the submit
 * that leaves Date blank for the same reason.
 */
/**
 * Is this draft on the FREE-TYPED destination branch? (board 2186)
 *
 * ⛔ ONE DEFINITION, used by the render AND by `sendRequest`. The sentinel is spelled in
 * `request-form-logic.ts:285` too, and a second inline `=== 'other'` here is how the control that
 * renders and the rule that blocks come to disagree about which branch they are on.
 */
function isOtherDestinationOf(values: RequestFormValues): boolean {
  return values.destinationId === 'other';
}

/**
 * The payment double-check's refusal, as an error map (board 2186). `{}` when there is nothing to
 * refuse.
 *
 * ⛔⛔ ONE DEFINITION, TWO GATES, AND THE FIRST GATE IS THE IDENTITY PROBE — NOT `sendRequest`.
 * `validateRequestInput` cannot carry this rule (D3 stores nothing, so the tick is not in the
 * payload), which means the probe in `submit` would otherwise pass a form that is complete EXCEPT
 * the tick — and `createTypedResident` would write an Article 9 `portal.residents` row for a
 * journey that is then refused. The app role holds no DELETE and erasure is an out-of-band runbook
 * act, so that row is PERMANENT. Measured by this change's own security gate, not theorised: the
 * probe passed, `POST /api/residents` fired, `POST /api/requests` never did, and the whole client
 * suite stayed green because no fixture reached the free-typed branch with a new resident.
 *
 * ⛔ THE INVARIANT IS GREPPABLE: every site must CALL this function. An inline
 * `!values.payerConfirmed` anywhere in this file is the drift, because the two gates guard
 * different hazards and would diverge silently.
 *
 * ⚠️ THE TWO PATHS DIFFER IN KIND, so do not "simplify" either away. On the `submit` path a NEW
 * Article 9 row is CREATED — that is the premature-creation hazard the probe gate exists for. On
 * the DD10 "put them back" path (`putBackResident` -> `sendRequest`, no probe) nothing is created:
 * an existing removed row is REACTIVATED by an explicit nurse act, which is the same ruling as the
 * roster write-back — a rectification is not contingent on a booking.
 */
function payerConfirmErrors(values: RequestFormValues): Record<string, string> {
  return payerConfirmShown(values) && !values.payerConfirmed
    ? { payerConfirmed: requestFormCopy.payerConfirmError }
    : {};
}

/**
 * ⭐ Board 2706 (Alex's ruling) — THE DOUBLE-CHECK IS FOR CASH / CARD ONLY. Its sentence says the
 * passenger pays by cash or card, so under HSE / Rehab or House account it asked the nurse to
 * confirm a payment that was not happening (George, LCC go-live). `self` is offered only on the
 * free-typed branch, so the branch test is kept for the reader, not for reachability.
 * ⛔ ONE PREDICATE FOR THE RENDER AND THE REFUSAL: a box that is not on screen must never be the
 * reason a send is refused, and a box on screen must never be skippable.
 */
function payerConfirmShown(values: RequestFormValues): boolean {
  return isOtherDestinationOf(values) && values.payerKind === 'self';
}

const ERROR_FIELD_IDS: ReadonlyArray<readonly [string, string]> = [
  ['requestedByStaffId', 'rf-requester'],
  ['residentId', 'rf-resident'],
  ['room', 'rf-room'],
  ['destinationId', 'rf-destination'],
  ['destinationText', 'rf-destination-text'],
  // ⭐ Board 2706 rework (R5): an unstated payer sent focus NOWHERE (the walk had no entry, so the
  // refusal rendered under a group she might have scrolled past). The target is the HSE / Rehab
  // radio because it is the first one ALWAYS offered (`allowedPayerKinds`, both classes).
  ['payerKind', 'rf-payer-hse'],
  // The payment double-check (board 2186). It renders only on the free-typed branch, between the
  // payer radios and the trip radios, so it sits here in screen order. Without an entry the error
  // is raised and `focusFirstError` walks past it, leaving a refusal the nurse cannot find.
  ['payerConfirmed', 'rf-payer-confirm'],
  // ⛔ THE NEXT-OF-KIN PAIR HAD TWO ENTRIES HERE AND BOTH ARE GONE (T1868 M6),
  // for the same reason `schedulePattern` below has none: their controls are
  // deleted, so `rf-nok-name` and `rf-phone` name ids that do not exist and the
  // focus walk would silently stop at nothing (ADR4-D10). ⚠ AND UNLIKE
  // `schedulePattern`, THE VALIDATOR NO LONGER RAISES THE KEYS EITHER — the pair
  // is not API-only-but-refused, it is absent, so there is no error left to
  // focus. The reasoning for their old ORDER is in git, not rotting in a list.
  // ⛔⛔ `schedulePattern` HAS NO ENTRY, AND THAT IS THE POINT (T1868 M2). Its
  // entry pointed at `rf-pattern-time`, chosen after two earlier repairs because
  // it was the one target whose distance to the message was invariant across the
  // four frequencies. M2 DELETES the recurring fieldset entirely — no frequency
  // select, no pattern time, no render site for `errors['schedulePattern']` — so
  // every candidate target is gone and an entry here would name an id that does
  // not exist. The reasoning is kept in git, not rotting in a list.
  // ⚠️ THE VALIDATOR STILL RAISES THE KEY, deliberately: a direct POST can still
  // carry a pattern and is refused by name. It is API-only and structurally
  // unrenderable, because the browser can no longer construct one.
  //
  // ⛔ `returnTrip` MOVED UP HERE, because the CONTROL moved. The trip-type radio
  // pair now renders ABOVE the date, where the schedule fieldset used to be, so
  // the list's top-to-bottom order — which IS the form's own, per ADR4-D10 —
  // requires it before `journeyDate`. It targets `rf-trip-oneway`, the FIRST
  // radio of the pair.
  // ⚠️ THE OLD ENTRY'S HAZARD DOES NOT TRANSFER, and the difference is worth
  // stating because the retired comment above warned about exactly this shape:
  // landing on the wrong radio used to be harmful because arrowing there
  // discarded a filled-in pattern. There is no pattern to discard now, and both
  // radios answer the SAME question, so focus on either is honest.
  ['returnTrip', 'rf-trip-oneway'],
  // ⛔⛔ T1868 M2 (wave-2 gate BLOCK) — `journey` HAD NO ENTRY, WHILE A COMMENT IN
  // `request-schema.ts` CLAIMED IT DID. The claim was written in the same change
  // that corrected this key's message, and the wiring it described was never
  // added: a comment asserting a ruling that does not exist, which is the exact
  // rot class that change was fixing.
  // ⛔ AND IT IS REACHABLE FROM A BROWSER, not a hand-built POST. Leave Date blank
  // and fill Time VALIDLY — a perfectly ordinary order for a nurse to work in —
  // and `hasJourneyDate === hasSchedulePattern` (both false) with a valid
  // `journeyTime` makes `errors['journey']` the SOLE error. Measured before the
  // fix: `document.activeElement.id` was `''` (focus never moved), `#rf-date`
  // carried no `aria-invalid` and no `aria-describedby`, and there is no HTML5
  // `required` on the control to pre-empt it. That is ADR4-D10's silent refusal —
  // "the page simply did not advance" — in the list whose job is to prevent it.
  // ⚠️ IMMEDIATELY BEFORE `journeyDate`, AND THE TWO SHARE A TARGET DELIBERATELY:
  // both mean "the outbound date is wrong or missing" and both resolve to
  // `rf-date`, so the walk lands on the same control whichever fires. `journey`
  // goes first because it is raised when the date is ABSENT ENTIRELY, which is the
  // more fundamental failure of the two.
  ['journey', 'rf-date'],
  ['journeyDate', 'rf-date'],
  ['journeyTime', 'rf-time'],
  // ⛔ THE MEANING KEY HAS NO ENTRY AND `pickupAt` TARGETS THE TIME CONTROL
  // (2026-09-13, Alex, board 2073). The arrive-or-collect radio and the pickup
  // pair are gone: the form asks the appointment time only, the meaning is sent
  // as the constant `'appointment'` (`emptyRequestFormValues`), and the pickup
  // is derived from the home's offset and never rendered. The validator still raises `pickupAt` — for a pickup
  // that has already passed (the appointment is too soon), or when the
  // derivation produced nothing — and the one control the nurse can act on is
  // the appointment time, so the message renders under `rf-time` and focus
  // lands there. ⚠️ `journeyTime` and `pickupAt` share the target the way
  // `journey` and `journeyDate` do; the validator makes them mutually exclusive
  // (the pickup block is gated on the journey instant being present), so the
  // walk never has to choose between them.
  // ⚠️ The validator still raises the meaning key for a direct POST that omits
  // it; a browser cannot produce that, and there is no control to focus.
  ['pickupAt', 'rf-time'],
  // ⛔⛔ THE RETURN BLOCK MOVED, SO ITS ENTRIES MOVED WITH IT (T1717 M5, DD4).
  // `returnTrip` used to sit between `destinationText` and `nextOfKinName`,
  // which was correct while the tick rendered beside "Where to". The whole block
  // now follows the schedule controls, so its keys follow theirs here — this
  // list is read top-to-bottom and its order IS the form's own (`ADR4-D10`), and
  // an entry left where the control no longer is would walk a nurse holding two
  // errors UPWARDS past the one she meets first.
  // ⚠️ `returnTrip` itself is reachable only from a non-browser caller: the
  // control is a checkbox, so a browser can only ever produce a boolean and
  // `errors['returnTrip']` needs a hand-built POST. Listed anyway — the entry
  // costs nothing and the alternative is a key whose focus target has to be
  // re-derived by whoever adds the next one.
  // ⛔ ONE CONTROL, TWO KEYS (2026-09-13, Alex, board 2073). The return is
  // same-day only, so the return date control is gone and `returnAt` — the
  // composed instant, whose errors are now "not a valid date and time", "must
  // be after the outbound trip" and the window — is answered at the TIME
  // control, the only one left. `returnTime` is raised when the time is blank
  // or malformed. Both target `rf-return-time`, as `journey` and `journeyDate`
  // both target `rf-date`.
  ['returnAt', 'rf-return-time'],
  ['returnTime', 'rf-return-time'],
  // ⭐ T2088 M4 (ADR-0006) — THE MARKER'S KEY, ANSWERED AT THE SAME CONTROL.
  // `returnAtState` is raised for a `to-confirm` marker sent beside a time, or
  // a marker whose value is not the one legal word; the time control is the
  // one the nurse can act on either way. ⚠️ The browser cannot reach it
  // (`assembleRequestPayload` sends the marker only when the time is blank),
  // so this entry is the hand-built-POST route, listed for the same reason
  // `returnTrip` is above: a key with no focus target is a key that leaves the
  // nurse with a message and nowhere to go.
  ['returnAtState', 'rf-return-time'],
  ['notes', 'rf-notes'],
];

/** ⭐ T2088 M4 (ux-review WARN) — the return time's STANDING hint, referenced by
 *  the control whether or not it is in error (see `errorPropsFirstDescribedBy`).
 *  A module constant, so the reference and the paragraph's own `id` are one
 *  string, exactly as `errorId` is for the messages. ⛔ NOT of the `errorId`
 *  shape (`rf-<field>-error`): it is not a message about a value, and a reader
 *  that mistook it for one would announce a standing sentence as a refusal. */
const RETURN_TIME_HINT_ID = 'rf-return-time-hint';

/** board 2732 M5 (plan Step 2): the most names the requester box offers at once; the count
 *  sentence states the cap when more match. */
const REQUESTER_SUGGESTION_CAP = 6;

/**
 * ⭐ T2075 M3 — WHERE THE FOCUS WALK LANDS WHEN ITS TARGET IS DISABLED (see
 * `focusFirstError`). Keyed by the control id in `ERROR_FIELD_IDS`, valued by
 * the control that has to change first for the disabled one to become
 * reachable. One entry, and it is not a pattern to grow by habit: a disabled
 * control that is ALSO the one in error is a shape to avoid, and this is the
 * one place the ruled two-select picker (plan D12) produces it.
 */
const FOCUS_WHEN_DISABLED: Readonly<Record<string, string>> = {
  'rf-requester': 'rf-requester-group',
};

/** `disabled` as the DOM reports it on the form controls this walk can reach. */
function isDisabled(el: HTMLElement): boolean {
  return (el as HTMLElement & { disabled?: boolean }).disabled === true;
}

/**
 * ⭐ A2-newrequest-4 (T1723 M4) — A DERIVED ID MAY NOT CARRY THE SPACE THE NAME
 * CARRIES. The equipment ids come from the equipment NAME, so `escort seat`
 * produced `id="rf-eq-escort seat"`. HTML forbids whitespace in an id and no
 * `#id` selector, `getElementById` walk or `document.querySelector` can address
 * it — it survives today only because the association is `for=`, which resolves
 * by string comparison. `focusFirstError` is exactly the kind of caller that
 * would silently find nothing if an equipment control were ever added to
 * `ERROR_FIELD_IDS`.
 *
 * ⛔ THE SAME FIX THE KIOSK ALREADY CARRIES, copied rather than invented:
 * `quote-helper/src/client/components/LocationSearch.tsx`'s `slugForIds`, which
 * routes a location label through this identical expression for this identical
 * reason. The fallback keeps the id non-empty for a name that is all
 * punctuation.
 */
function slugForId(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'option';
}

/**
 * The picker label for one roster match: the name, then the room when there is
 * one. Rooms run per floor and staff share similar names, so a name-only chip
 * gives a nurse nothing to tell two namesakes apart (board 1861, which carries
 * the home and the meeting it came from).
 *
 * ⛔ THE ROOM GOES AFTER THE NAME, NEVER BEFORE.
 * `e2e/tests/portal/portal-device-fit.spec.ts:113` matches this chip with a
 * PREFIX-ANCHORED `/^Test Resident /`: a suffix still matches, a prefix breaks
 * the e2e net. The order is held by the exact-`textContent` arm in
 * `test/client/request-form.spec.tsx`, not by this comment.
 *
 * ⛔ A BLANK ROOM RENDERS THE NAME ALONE, never a bare separator.
 * `portal.residents.room` is `TEXT NOT NULL` with NO non-blank CHECK
 * (`core/migrations.ts:51`), and `roomLine('')` returns `'Room '` — a label
 * over nothing. The emptiness is therefore decided HERE, before `roomLine` is
 * called, and cannot be delegated to it. The rule is the one v12's own column
 * comments state for the pickup line — omit the label when there is no value,
 * because a separator or a heading over nothing reads as a DROPPED field rather
 * than an absent one.
 * ⚠️ THIS PARAGRAPH USED TO CITE `opsCopy.nextOfKinCell` as its precedent, twice.
 * T1868 M6 deleted that function with the rest of next of kin; the rule it
 * illustrated is unchanged, so it is re-anchored rather than struck.
 *
 * ⚠️ The `typeof` guard defends the WIRE, whose type is a declaration and not a
 * guarantee — `meta` is a network response, so this check cannot take its false
 * branch under the declared type and is defensive rather than load-bearing.
 */
function residentPickerLabel(r: MetaResident): string {
  const room = typeof r.room === 'string' ? r.room.trim() : '';
  return room === '' ? r.displayName : `${r.displayName} · ${myResidentsCopy.roomLine(room)}`;
}

export function RequestForm({
  api,
  onLock,
  onSentChange,
  role = 'group',
  groupLabel = null,
  initialDraft,
  onDraftChange,
  onSendPhase,
  sendPending: sendPendingProp,
  initialSent,
  accountId,
}: RequestFormProps) {
  // ⛔ READ ONCE: the shell remounts this form when the pending send settles, so a later prop
  // value must not re-disable (or re-enable) the instance that pressed Send itself.
  const [sendPending] = useState(sendPendingProp === true);
  /** ⭐ Board 2706 rework (R1, round 2) — read by a send's async chain AFTER each await: is this
   *  instance still on screen, and who is signed in now (`stillMine`). Refs, never state. */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const accountRef = useRef(accountId);
  accountRef.current = accountId;
  const [meta, setMeta] = useState<MetaResponse | null>(null);
  // ⚠️ DERIVED HERE, beside the state it reads, rather than after the loading
  // gate below: declaring them lower would put them textually BELOW `submit`,
  // which closes over them. Safe either way, needlessly subtle one way. The
  // optional chain makes this valid before the gate; the gate still guarantees
  // meta is present at every render underneath it.
  const residentsWritable = meta?.residentsWritable ?? false;
  /** ⭐ T1868 M5 — the home's stated offset, or null when there is nothing to
   *  derive from. `?? null`, never `?? 60`: see `MetaResponse.home`. */
  const pickupOffsetMinutes = meta?.home?.pickupOffsetMinutes ?? null;
  const [metaError, setMetaError] = useState<string | null>(null);
  /**
   * ⭐⭐ Board 2638 (plan D3) — THE SERVER'S CLOCK, ESTIMATED WITHOUT THE DEVICE'S.
   * `serverNow` plus the monotonic time elapsed since it arrived. It can LAG the
   * server (the response's own latency, and on WebKit `performance.now()` stops
   * while the device sleeps) but never run ahead, and moving the device clock
   * does not move it. That is why the browser ordinarily applies only the LOWER
   * bounds (`lowerBoundsOnly`): a lag can only make those more lenient, so a
   * wrong device clock cannot block a valid request. The server re-checks all five.
   * ⛔ ONE EXCEPTION, THE NEW-RESIDENT PROBE IN `submit`, WHICH APPLIES ALL FIVE.
   * It runs just after a fresh `/api/meta` re-anchor, so the estimate then lags
   * only by that round trip, and the only journey an upper bound could wrongly
   * refuse would sit within milliseconds of the 28-day edge. Skipping them there
   * would let a journey the server must refuse write the PERMANENT Article 9
   * roster row first (review round 1, board 2638).
   * A REF: nothing renders it.
   */
  const serverClock = useRef<{ serverMs: number; perfMs: number }>({ serverMs: 0, perfMs: 0 });
  /** `serverNow` as epoch milliseconds, or null when it is absent or unparseable. */
  function parseServerNow(serverNow: unknown): number | null {
    const serverMs = typeof serverNow === 'string' ? Date.parse(serverNow) : Number.NaN;
    return Number.isNaN(serverMs) ? null : serverMs;
  }
  /** Re-anchors the estimate. An unreadable `serverNow` anchors at the epoch:
   *  the most lenient estimate, and the server re-checks every bound. */
  function recordServerClock(serverNow: unknown): void {
    serverClock.current = { serverMs: parseServerNow(serverNow) ?? 0, perfMs: performance.now() };
  }
  function estimatedServerNow(): Date {
    return new Date(serverClock.current.serverMs + (performance.now() - serverClock.current.perfMs));
  }
  // ⚠️ `values` is declared on the next line, so the collection preview cannot be
  // derived here beside its siblings; it is declared immediately after, which is
  // as close as the dependency allows.
  const [values, setValues] = useState<RequestFormValues>(() => initialDraft?.values ?? emptyRequestFormValues());
  /**
   * ⭐ THE COLLECTION THE NURSE SEES (board 2188, Alex 2026-09-18). It REVERSES
   * R-2073-c, which said she SHALL NOT see this guess.
   *
   * ⛔ IT READS THE PAIR `derivePickup` WROTE, AND NEVER RE-DERIVES. A second
   * `derivePickup` call here would be a second source of truth that disagrees
   * exactly when the last derivation failed — which is why `setAppointmentField`
   * now clears the pair instead of leaving it stale.
   * ⭐ Board 2638: the pair is no longer SUBMITTED; the server derives the stored
   * collection with the same functions (`dublinInstant`, `pickupInstantFor`)
   * over the same offset, and `derivePickup` reads it back as IRISH time, so
   * what is previewed is what is stored whatever zone the device is set to.
   * ⚠️ Empty string, not null, when there is nothing to show: no offset for this
   * home, a half-typed appointment, or an invalid instant. The render below shows
   * NOTHING in that case — never a placeholder.
   *
   * ⛔⛔ AND IT CARRIES ITS OWN DAY WHEN THAT DAY DIFFERS (security review M1,
   * board 2188). The first version of this dropped `values.pickupDate` and
   * previewed the clock alone, under the APPOINTMENT'S date — the same defect M1
   * found on the three read surfaces, left on the one surface that creates the
   * value. It is reachable on legitimate configuration, with no crafted body:
   * `homes_pickup_offset_minutes_range` permits 480 minutes, so a 07:30
   * appointment derives 23:30 the night before and the form would preview
   * `Collection 23:30` above a date line reading the next day.
   * ⚠️ A STRING COMPARE, not `dublinDayNumber`, and that is the difference from
   * the three siblings rather than an inconsistency with them: they hold ISO
   * INSTANTS off the wire, while these two are the form's own local
   * `YYYY-MM-DD` control values, already in the same calendar. The rule is
   * identical — show the day only when it differs — and the formatter is the
   * same `formatWhenParts`, whose `date` is safe on a bare local date because
   * Dublin is never behind UTC, so a UTC-midnight parse cannot fall back a day.
   */
  const collectionPreview =
    values.pickupDate !== '' && values.pickupTime !== ''
      ? values.pickupDate === values.journeyDate
        ? values.pickupTime
        : `${values.pickupTime} (${formatWhenParts({ journeyDate: values.pickupDate }).date})`
      : '';
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * ⭐ `ADR4-D10` (T1033 M8) — SEND THE NURSE TO THE FIRST PROBLEM.
   *
   * ⛔ WHY THIS IS A REAL DEFECT AND NOT POLISH. This form is long enough to
   * scroll on a ward tablet, and until now a rejected submit changed nothing
   * the user was looking at: the button sat at the bottom, the first error
   * could be several screens up, and nothing moved the caret or the viewport.
   * A keyboard or screen-reader user got no signal at all that the submit had
   * been refused — the page simply did not advance.
   *
   * ⚠️ THE MAP IS ORDERED, AND THE ORDER IS THE FORM'S OWN. `Object.keys` on
   * the errors object is insertion-ordered by whatever the validator happened
   * to check first, which is NOT what the nurse sees. "First" has to mean
   * first ON SCREEN, so the walk is over this list, top to bottom.
   *
   * ⚠️ It focuses the CONTROL, not the error text: the control is what she has
   * to change, focusing it announces the field's own label and its
   * `aria-invalid` state, and `focus()` scrolls it into view for free — which
   * is the third thing D10 asks for.
   */
  function focusFirstError(errs: Record<string, string>): void {
    for (const [key, id] of ERROR_FIELD_IDS) {
      if (!errs[key]) continue;
      const el = document.getElementById(id);
      // ⚠️ A field can legitimately be absent — the schedule branch swaps
      // one-off and recurring controls in and out — so a missing node means
      // "keep looking", never "give up".
      if (!(el instanceof HTMLElement)) continue;
      // ⛔⛔ A DISABLED CONTROL CANNOT TAKE FOCUS (T2075 M3, ux-review BLOCK,
      // reproduced live on the reception shape): `.focus()` on one silently
      // no-ops, and a walk that returned here had sent her NOWHERE — the
      // message rendered, the viewport stayed at the Send button, and a
      // keyboard or screen-reader user got no signal at all, which is the
      // exact failure ADR4-D10 exists to end. The one such control on this
      // form is the requester name select before a group is chosen, and the
      // control she must fix FIRST is then the group select above it; the
      // walk lands there instead. Any other disabled target keeps looking.
      const target = isDisabled(el) ? document.getElementById(FOCUS_WHEN_DISABLED[id] ?? '') : el;
      if (target instanceof HTMLElement && !isDisabled(target)) {
        target.focus();
        return;
      }
    }
  }
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /**
   * ⛔ `requestId` AND `alertFailed` ARE CARRIED, NOT DISCARDED (T1101). Both
   * were already typed in the `api.post` generic below and thrown away; the
   * reference number is the one thing a nurse can quote on the phone when the
   * office was not reached, so it has to survive into the panel.
   */
  const [success, setSuccess] = useState<RequestSentFacts | null>(initialSent ?? null);
  // The one Resident field (2026-07-21 review, P8): what the nurse typed.
  // A roster match selects that resident; an unmatched name becomes a NEW
  // resident on send — no separate add-a-resident panel, no second room
  // field, no forced pick.
  const [residentQuery, setResidentQuery] = useState(initialDraft?.residentQuery ?? '');
  /**
   * ⛔ WHAT THE STANDING `errors.residentId` IS ABOUT: a REF, never state.
   *
   * `null` = no refusal recorded through `refuseResident` (so `onResidentInput`
   * keeps its pre-M4d behaviour and clears, which is what `'Select a resident.'`
   * needs); a string = the resident text that refusal was about; `NOT_ABOUT_TEXT`
   * = a capability refusal no text can answer.
   * ⚠️ A REF BECAUSE IT NEVER RENDERS. Nothing on screen reads it; it only
   * decides what the NEXT keystroke is allowed to clear, and making it state
   * would re-render the whole form on a value no pixel depends on.
   */
  const residentRefusedAbout = useRef<string | typeof NOT_ABOUT_TEXT | null>(null);
  /**
   * ⭐ DD10 — the removed resident the server named on a 409, and the offer to
   * put them back. `null` the rest of the time, so nothing about a removed
   * person is on screen unless the nurse typed that person's name herself.
   *
   * ⚠️ IT HOLDS AN ID THAT CAME FROM THE SERVER, and that disclosure is
   * deliberate and bounded: the lookup behind it is home-scoped, so it is always
   * this nurse's own home's row. Within the home it makes the removed set
   * discoverable by name, which is the mechanism keeping one person to one row —
   * and it is the same unmetered nurse surface M2/M3 carried to the M7 hand-off
   * as a P2 row, not a new hole.
   */
  const [removedOffer, setRemovedOffer] = useState<{
    residentId: number;
    displayName: string;
  } | null>(null);
  /** ⛔ SEPARATE FROM `errors`, because it is not a field problem: the request
   *  went, only the OPTIONAL roster write-back was refused (P1-2). Rendering it
   *  in `errors['room']` would mark a valid control invalid. */
  const [rosterUpdateNotice, setRosterUpdateNotice] = useState<string | null>(null);

  /** ⭐ Board 2647 (P2): returns the body it read, or null when the read
   *  failed, so a caller that must act on the CURRENT home offset in the same
   *  handler can: `setMeta` does not reach that handler's closure. */
  const loadMeta = async (): Promise<MetaResponse | null> => {
    setMetaError(null);
    const res = await api.get<MetaResponse>('/api/meta');
    if (res.kind === 'network-error') {
      setMetaError(sharedCopy.networkError);
      return null;
    }
    if (res.status !== 200) {
      setMetaError(sharedCopy.genericError);
      return null;
    }
    recordServerClock(res.body.serverNow);
    setMeta(res.body);
    return res.body;
  };

  useEffect(() => {
    void loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ⛔⛔ THE GOOD-NEWS PATH HAD NO ANNOUNCEMENT AT ALL (T1683 M3 review, F3),
   * and it was the COMMON path. On submit the form unmounts wholesale, so
   * `document.activeElement` falls back to BODY: a screen-reader nurse got no
   * signal that anything had happened and never heard the reference she is
   * meant to ring about. Meanwhile `alertFailedNotice` - the RARE
   * office-not-reached case - carries `role="alert"` and is announced. The bad
   * news was louder than the good news.
   *
   * ⭐ THIS IS THE KIOSK'S EXISTING PATTERN, ADOPTED RATHER THAN INVENTED:
   * `quote-helper/src/client/App.tsx` focuses `.screen-title` on every screen
   * transition, and every kiosk `<h1>` carries `tabIndex={-1}` to receive it.
   * The portal never took it up. Scoped to THIS screen deliberately - the wider
   * retrofit is a separate concern and is not this fix.
   *
   * ⛔ WHY NOT A `role="status"` WRAPPER, which would be fewer lines: a live
   * region that is INSERTED in the same render as its content is unreliably
   * announced (the region has to exist before the content changes), and this
   * panel replaces the form outright. Moving focus is the mechanism that does
   * not depend on that timing - and it also puts the reading cursor directly
   * above the reference, so continuing to read reaches it.
   */
  const successTitleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (success !== null) successTitleRef.current?.focus();
  }, [success]);

  /**
   * ⛔ II-portal-#27 — REPORTED FROM AN EFFECT, NOT FROM `submit`. The panel is
   * left by three routes (`startAnother`, a fresh mount when the nurse comes
   * back to this tab, and the send itself), and only the rendered `success`
   * state is true on all three. Telling the shell from `submit` alone would
   * leave the tab unpressed for the rest of the session after "Another
   * request".
   * ⚠️ `onSentChange` is read from the props object each run, so a caller that
   * passes a fresh closure every render does not re-fire this: the dependency is
   * the state, which is the fact being reported.
   *
   * ⛔⛔ AND IT REPORTS `false` ON THE WAY OUT - the M4 output gate's second
   * finding. The flag says "the sent panel is on view", and this component is
   * the only thing that can see it, so leaving without saying so left the shell
   * believing a panel that no longer exists. Measured: send a request, then tap
   * `My requests` or `My residents`. RequestForm unmounts with `success` still
   * set, the effect never runs again, and `requestSent` stays true for the rest
   * of the session - so NO tab is pressed on any screen she visits afterwards,
   * while a screen is plainly on view.
   * ⚠️ THE CLEANUP ALSO RUNS BETWEEN VALUES, not only on unmount, and that is
   * harmless by ordering: preact runs the previous cleanup before the new
   * effect, so a `null -> sent` transition reports `false` then `true` in one
   * commit and settles on `true`.
   * ⚠️ WHY NOT DERIVE IT IN THE SHELL instead: the shell cannot see `success`,
   * which is this component's own state, so deriving it there means either
   * lifting the state or re-deriving the panel's existence from something that
   * is not the panel. The report already exists; it only needed its other half.
   */
  useEffect(() => {
    onSentChange?.(success !== null);
    return () => onSentChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  /**
   * ⭐⭐ II-portal-#12 (T1723 M4) — FIXING A FIELD CLEARS THE THING SHE WAS TOLD
   * ABOUT IT, AND THE WRITE IS WHAT CLEARS IT.
   *
   * ⛔ WHAT THE DEFECT WAS, measured live at M2: after a refused submit, typing
   * `12A` into Room left "Room is required." standing under a field that now
   * held a room, and the control kept `aria-invalid="true"` -- so the screen
   * asserted a problem the nurse had already fixed, and a screen reader kept
   * announcing the field as invalid. Errors only ever moved on the next submit.
   *
   * ⛔⛔ AND A BARE `clearFieldError` HELPER WAS NOT ENOUGH, which the output
   * gate proved: three write paths remembered to call it and a FOURTH did not.
   * `applyResidentMatch` writes Room and the next-of-kin pair from the matched
   * roster row, so an exact-name match auto-filled Room to `4B` under a standing
   * "Room is required." with `aria-invalid="true"` still on the control. That is
   * the SAME defect through the one door that never types a character.
   * ⛔ SO THE VALUE WRITE AND THE CLEAR ARE ONE ACT. Every path that writes a
   * user-visible field value goes through `writeFields`, which takes the error
   * keys the write answers; there is no way to move a value without saying which
   * message that settles. A fifth write path cannot repeat the omission because
   * there is nowhere else to write from.
   *
   * ⛔ ONLY THE NAMED KEYS, NEVER THE WHOLE MAP. Clearing `errors` wholesale on
   * any keystroke would wipe the other fields' messages while she is still
   * reading them, which is the same silence from the other direction.
   * ⚠️ THE KEYS ARE ERROR KEYS, NOT VALUE KEYS, and they are not always the
   * same word: `setReturnTrip` writes `returnTime` and settles
   * `returnAt` / `returnTime`, because the instant is composed from it and the
   * journey date. A caller passing value keys blindly would clear the wrong
   * message.
   * ⚠️ Error keys with no control of their own (`journey`, `schedulePattern`)
   * are untouched by construction: no write path names them.
   * ⚠️ RETURNS `prev` UNCHANGED when there is nothing to clear, so an ordinary
   * keystroke on a valid form does not re-render the whole form.
   * ⚠️ THE UPDATE IS OPTIONAL for the one control whose value does not live in
   * `values`: the Resident box renders `residentQuery`, so `onResidentInput`
   * writes that and comes here for the clear alone. `startAnother` is
   * deliberately NOT a caller either - it blanks the form and drops the whole
   * error map, which is a reset rather than a field write.
   *
   * ⭐⭐ AND FOR THE RESIDENT BOX, "THE VALUE MOVED" IS NOT "A CHARACTER
   * ARRIVED" - T1723 M4d, security review (LOW). `errors.residentId` holds four
   * different truth conditions (see `NOT_ABOUT_TEXT` at the top of this file):
   * a blank box, the specific TEXT she sent, and capability facts about the
   * home that no keystroke can answer. Clearing all four on any keystroke told
   * a nurse that "Your list is full." had gone away because she typed a letter.
   * ⛔ SO A REFUSAL CARRIES ITS PROVENANCE, and this stays the ONE clearing
   * door: whenever `residentId` is among the settled keys the provenance goes
   * with the message, because a message and the reason it may be cleared are
   * one fact and must not be able to drift apart. `onResidentInput` asks the
   * provenance first and only reaches here when the text has genuinely MOVED
   * away from what the refusal was about; a capability refusal instead leaves
   * through `applyResidentMatch` (an identity was picked, so the fact is being
   * re-decided) or on the next Send, which re-derives every message from
   * scratch.
   * ⚠️ THE REF IS WRITTEN OUTSIDE THE UPDATER, the R3 rule `applyResidentMatch`
   * states at length: an updater may run more than once for one logical update,
   * and a ref mutation inside it would apply once per invocation.
   */
  function writeFields(written: readonly string[], update?: Parameters<typeof setValues>[0]): void {
    if (update !== undefined) setValues(update);
    if (written.includes('residentId')) residentRefusedAbout.current = null;
    setErrors((prev) => {
      if (!written.some((k) => prev[k] !== undefined)) return prev;
      const next = { ...prev };
      for (const k of written) delete next[k];
      return next;
    });
  }

  function setField<K extends keyof RequestFormValues>(key: K, value: RequestFormValues[K]): void {
    writeFields([key as string], (prev) => ({ ...prev, [key]: value }));
  }

  /**
   * ⛔⛔ THE ONE WRITER OF A SERVER-REPORTED RESIDENT REFUSAL, and it records
   * what the refusal was ABOUT in the same act that puts the message up.
   *
   * ⛔ ONE FUNCTION FOR THE REASON `writeFields` IS ONE FUNCTION. The message
   * and its provenance are two halves of one fact; two separate calls at five
   * sites is five chances to write the message and forget the provenance, which
   * is exactly the omission the M4 output gate found on the VALUE side (a
   * fourth write path that never cleared). There is nowhere else to set
   * `errors.residentId` from a server answer, so a sixth refusal cannot repeat
   * it.
   * ⚠️ `about` IS THE CALLER'S TO STATE, never read from `residentQuery` in
   * here: `createTypedResident` refuses about the name it SENT, which is the
   * trimmed text as it stood when Send was pressed, and the box may
   * legitimately have moved on by the time an await returns.
   */
  function refuseResident(message: string, about: string | typeof NOT_ABOUT_TEXT): void {
    setErrors((prev) => ({ ...prev, residentId: message }));
    residentRefusedAbout.current = about;
  }

  /**
   * ⛔ THE THREE WHOLESALE WRITES, WHICH REPLACE THE MAP RATHER THAN ADD TO
   * IT: the submit probe (`validateRequestInput`), the server's own 400, and
   * `sendRequest`'s pre-POST revalidation. Their resident message is the SHARED
   * validator's `'Select a resident.'`, which is about the TEXT IN THE BOX -
   * normally blank - so the provenance is that text as it stands at the moment
   * of the write, and the next keystroke that moves it away legitimately clears
   * the message.
   * ⚠️ AND IT MUST ALSO CLEAR when the new map carries NO resident message: a
   * stale provenance left over from a refusal the validator has just answered
   * would make the next keystroke consult a fact about a message that is no
   * longer on the screen.
   * ⚠️ THE THIRD WRITE CANNOT CARRY A RESIDENT ERROR TODAY - every caller
   * reaches `sendRequest` with a resident id the roster has already validated -
   * and it is recorded anyway so the invariant "every wholesale error write
   * records provenance" holds by construction rather than by call-graph luck.
   */
  function recordWholesaleResidentRefusal(errs: Record<string, string>): void {
    residentRefusedAbout.current = errs['residentId'] === undefined ? null : residentQuery;
  }

  /**
   * ⛔⛔ UNTICKING THE RETURN CLEARS THE TIME, IN THE SAME STATE CHANGE — T1717
   * M5, webapp-testing FAIL. (It cleared a date/time PAIR until 2026-09-13,
   * Alex, board 2073, when the return became same-day only and the date
   * control left; the rule is unchanged for the control that remains.)
   *
   * ⛔ WHAT THE DEFECT WAS. The control unmounts when the tick goes off but its
   * VALUE survived in state, so ticking again re-revealed a time the nurse had
   * already withdrawn — pre-filled, plausible, and never restated. That is
   * T632's defect through a different door: the rule for these controls is
   * blank by default, and "default" has to mean every time they appear.
   * ⚠️ ONE `setValues`, not two `setField`s: the tick and the time have to move
   * together or a render between them shows a ticked return with a stale time.
   * ⚠️ There is no `returnAt` in form state to clear — the instant is composed at
   * assembly time from the journey date and this time, and
   * `assembleRequestPayload` gates that composition on the tick as well, so an
   * untick cannot post an instant even if the time somehow survived.
   *
   * ⛔ AND IT CLEARS THE TWO ERROR KEYS AS WELL — T1717 M7, Captain deltas #37.
   * The VALUES were cleared here from M5 but `errors.returnAt` / `errors.returnTime`
   * were not, so submit-with-errors → untick → re-tick re-revealed two BLANK
   * inputs still carrying `aria-invalid` and the previous message: an error
   * about a value that no longer exists, on a control the nurse has just reset.
   * The M5 untick/re-tick spec never submitted, so it could not see this.
   * ⚠️ A SIBLING GAP RECORDED HERE IS NOW MOOT, and is kept as a note rather than
   * a warning: it said toggling `scheduleMode` had the same shape (changing which
   * outbound controls are live without clearing their errors) and was "deliberately
   * not fixed here". T1868 M2 DELETED `scheduleMode`, so there is no sibling left
   * to fix — the trip-type pair routes through THIS function, which does clear.
   */
  /**
   * ⭐⭐ T1868 M5 (P0-4) — THE APPOINTMENT MOVES, SO THE OFFERED PICKUP MOVES
   * WITH IT — **UNLESS THE NURSE HAS TOUCHED IT**.
   *
   * ⛔⛔ NOT AN EFFECT, AND THAT IS THE RULE RATHER THAN A PREFERENCE. Syncing
   * one piece of state to another with `useEffect` runs AFTER a render, so there
   * is a frame in which the form shows an appointment and a pickup that do not
   * belong together — and, worse, it fires on every cause of a change including
   * the nurse's own edit, which is exactly the clobber this function exists to
   * prevent. The recomputation belongs in the ACT that changed the appointment,
   * in one `setValues`, so the pair is never observably inconsistent. It is the
   * same reasoning `setReturnTrip` below records for clearing the return pair.
   *
   * ⛔ `pickupTouched` IS THE WHOLE GATE, and it is read from `prev` INSIDE the
   * updater rather than from the closure: two keystrokes in the same tick would
   * otherwise both see the pre-edit flag.
   *
   * ⚠️ IT DOES NOT CLEAR ON A FAILED DERIVATION. If the appointment goes blank
   * or the home publishes no offset, `derivePickup` returns null and the
   * previously derived value STAYS. That is harmless since the pair stopped
   * rendering (2026-09-13, Alex, board 2073): a blank appointment is refused
   * before the pickup is read, and the next complete appointment re-derives.
   * ⛔ `pickupTouched` CAN NO LONGER BECOME TRUE — the nurse-facing writer
   * (`editPickupField`) left with the controls — so the gate is always open and
   * the pickup is always the form's. The flag stays in state so the derivation's
   * shape is unchanged; a re-introduced control is the only thing that would
   * set it.
   */
  /**
   * ⛔⛔ AN ERROR ABOUT A VALUE THE **FORM** HAS JUST REWRITTEN IS A FALSE
   * STATEMENT, and clearing it is `setReturnTrip`'s existing pattern (T1717 M7,
   * Captain deltas #37: "an error about a value that no longer exists, on a
   * control the nurse has just reset").
   *
   * ⛔ FOUND LIVE BY THE M5 ux REVIEW, AT HIGH, AND IT WAS REAL (on the
   * since-deleted pickup pair and meaning radio; the rule outlives them). Submit
   * with the pickup pair blank → refused. Tap the collect radio and back → the
   * switch re-derived a fresh, VALID pair (measured: `2026-09-15` /
   * `05:15`) and the two stale errors survived. A sighted nurse saw a filled-in,
   * gold-tinted, obviously-correct date wearing a red border and "Say what day
   * the bus picks them up." A screen-reader user got worse: the control
   * announced as invalid, WITH a stated reason, while its value was spoken as a
   * real date — a self-contradictory announcement with no visual cross-check.
   *
   * ⛔ THE RULE, STATED SO THE NEXT CONTROL DOES NOT REPEAT IT: clear the errors
   * for the values THIS FUNCTION WRITES, and only those. Ordinary typing does
   * NOT clear its own error anywhere on this form — that is the house idiom and
   * this is not a licence to change it. The distinction is authorship: an error
   * about what SHE typed stands until she resubmits; an error about what the
   * FORM typed over her is a claim the form is contradicting in the same breath.
   */
  function clearErrors(...keys: string[]): void {
    setErrors((prev) => {
      if (keys.every((k) => prev[k] === undefined)) return prev;
      const next = { ...prev };
      for (const k of keys) delete next[k];
      return next;
    });
  }

  function setAppointmentField(key: 'journeyDate' | 'journeyTime', value: string): void {
    /* ⛔⛔ THE KEY SHE TYPED CLEARS FIRST, AND ITS ABSENCE WAS A LIVE DEFECT (T1749
       M10 rework 1, Captain's live pass item 5). Every other control on this form
       writes through `writeFields`, which deletes the error for the key it wrote —
       "a message survives only until she touches the field it is about". This
       function hand-rolls `setValues` instead, because it needs a flag out of
       the updater, and the error clear was lost with it. The
       consequence a nurse saw: type an invalid time, Send, get "Enter the time in
       24-hour format, like 09:30."; correct it to 10:30 and the field KEEPS its red
       border and its message while the submit succeeds — a form telling her she is
       wrong about a value it has just accepted.
       ⚠️ CLEARED ON THE EDIT, not on a validity test, and deliberately: this form
       has ONE rule for when a field error dies and it is `writeFields`'s. A second
       rule that waited for validity would make these four controls behave unlike
       every sibling, and the message is re-derived from scratch on the next Send
       anyway. */
    clearErrors(key);
    let rederived = false;
    let cleared = false;
    setValues((prev) => {
      const next = { ...prev, [key]: value };
      if (prev.pickupTouched) return next;
      const derived = derivePickup(next.journeyDate, next.journeyTime, pickupOffsetMinutes);
      // ⛔⛔ IT NOW CLEARS ON A FAILED DERIVATION, AND THE NOTE THAT PERMITTED
      // THE DRIFT IS RETIRED (board 2188, Alex 2026-09-18). It used to read:
      // "It DOES NOT CLEAR ON A FAILED DERIVATION... the previously derived
      // value STAYS. That is harmless since the pair stopped rendering
      // (2026-09-13, Alex, board 2073)." The ground was the not-rendering, and
      // this board makes it render again, so keeping a stale pair would show
      // the nurse a collection derived from an appointment she has since
      // changed or emptied.
      // ⚠️ DISPLAY ONLY, and the submit-side claim was REFUTED at plan review:
      // this branch re-derives on EVERY valid edit, so a stale pair survives
      // only while the derivation fails, and that state cannot reach the server
      // (a submit-eligible date/time pair is never blank, and a null or
      // non-positive offset means the pair was never populated at all).
      if (derived === null) {
        cleared = true;
        return { ...next, pickupDate: '', pickupTime: '' };
      }
      rederived = true;
      return { ...next, ...derived };
    });
    // ⛔ ON EITHER WRITE, never on the re-derivation alone. The old rule was
    // "only when it actually rewrote the pickup", justified by "a failed
    // derivation leaves the previous value alone, so any error about it is
    // still true" — which the clearing branch above has just made false. An
    // error about a value THIS FORM has just erased is the false statement the
    // writeFields rule forbids.
    if (rederived || cleared) clearErrors('pickupAt');
  }

  /**
   * The destination writer (board 2186). It exists because the CLASS of the destination decides
   * which payer kinds are offered, so changing it can strand a payer the nurse can no longer pick.
   *
   * ⛔ CLEARS `payerKind` AND `payerConfirmed` IN THE SAME UPDATE. Before this, nothing reset the
   * payer: a nurse could choose Somewhere else, pick Cash / card, switch back to a curated
   * destination and submit `payerKind: 'self'` — which the server now refuses and which the kiosk
   * crossing would have refused with a 409 anyway. Two writes instead of one would leave a render
   * in which the tick outlives the payer it was confirming.
   *
   * ⭐ It clears the payer on EVERY class change rather than only on a disallowed one: re-picking
   * is one click, and silently keeping a payment method across a change of destination is the
   * surprise this row exists to remove.
   */
  function setDestination(next: string): void {
    const classChanged = (next === 'other') !== isOtherDestinationOf(values);
    writeFields(['destinationId', 'payerKind', 'payerConfirmed'], (prev) => ({
      ...prev,
      destinationId: next,
      payerKind: classChanged ? '' : prev.payerKind,
      payerConfirmed: classChanged ? false : prev.payerConfirmed,
    }));
  }

  function setReturnTrip(on: boolean): void {
    // ⛔ THE PAIR'S ERROR KEYS, NAMED HERE AND NOWHERE ELSE. `returnAt` is
    // the composed instant and has no control of its own, so this is the only
    // write that can settle it - which is exactly why the keys are the caller's
    // to state rather than derived from the value keys.
    // ⭐ T2088 M4 — `returnAtState` joins them: the marker is composed from the
    // tick and the blank time, so a flip of the tick settles its error exactly
    // as it settles the other two.
    writeFields(['returnAt', 'returnTime', 'returnAtState'], (prev) => ({
      ...prev,
      returnTrip: on,
      returnTime: on ? prev.returnTime : '',
    }));
  }

  /**
   * Does what the nurse typed name a resident already on the roster?
   *
   * ⛔ THE STORE'S OWN RULE, IMPORTED - NOT `trim().toLowerCase()`, which is what
   * stood here and stranded a nurse. Names are stored `cleanStoredText`-cleaned
   * (interior whitespace collapsed), so a name typed with a double space matched
   * NOTHING once the resident created from it came back - and on a retry the R1
   * settle fired, cleared the fields authored under that resident, and left her
   * with required-field errors and nowhere to go, because the server correctly
   * refuses a second create as `resident_exists`. `sameStoredName` reproduces the
   * server's composition (`cleanStoredText` at the route, then `lower(btrim(...))`
   * in `sameNameInHome`) rather than approximating it a third way.
   */
  function matchResident(query: string): MetaResident | undefined {
    if (query.trim().length === 0) return undefined;
    return meta?.residents.find((r) => sameStoredName(r.displayName, query));
  }

  /** The fields prefill owns. ⚠ ONE SINCE T1868 M6, and it stays a NAMED LIST
   *  rather than collapsing to a bare `'room'`: every rule below is written
   *  against the list, so a second prefilled field is an entry here rather than
   *  a re-derivation of the authorship machinery. The machinery is not
   *  overhead — the harm it exists to stop (a corrected room travelling to a
   *  DIFFERENT resident) was measured on `room` itself. */
  const PREFILLED_FIELDS = ['room'] as const;

  /**
   * ⭐⭐ WHO AUTHORED EACH PREFILLED FIELD — the PM's ruling of 2026-09-03, and it
   * replaces the value-comparison snapshot that stood here.
   *
   * ⛔ THE RULE IS "A VALUE BELONGS TO THE RESIDENT IT WAS ENTERED UNDER, AND
   * NEVER TRAVELS TO ANOTHER." The previous rule was authorship-in-the-abstract:
   * anything the nurse had edited was hers and survived every match change. The
   * live re-check measured the harm — type A (room autofills), correct the room,
   * then type C's exact name: next of kin switched to C's and the corrected room
   * STAYED, so `POST /api/requests` carried a room for C that belongs to A.
   *
   * ⛔ SO "EDITED" IS NOT ENOUGH; the question is edited **under whom**. Three
   * authors, and each is a different answer at the next match:
   *   · `PREFILL`      — the form wrote it. Follows the match, always.
   *   · a resident id  — she typed it while THAT resident was matched. It is
   *                      about that person and does not travel to another.
   *   · `''`           — she typed it with NOBODY matched, i.e. while describing
   *                      the person she is about to name. If the resident she
   *                      then names has no value of their own, hers stands.
   *
   * ⛔ RECORDED AT EDIT TIME, NOT INFERRED BY COMPARING VALUES. A comparison
   * cannot tell "she typed the same room the roster already held" from "she never
   * touched it", and — the reason that matters — it cannot say WHO was matched
   * when she typed. That is the whole question now.
   *
   * ⚠️ A REF, NOT STATE: nothing renders from the authors directly (the tint and
   * the status line are DERIVED from them), and it must be readable by the very
   * next keystroke's handler rather than after a re-render.
   */
  const PREFILL_AUTHOR = 'prefill';
  const fieldAuthor = useRef<Record<(typeof PREFILLED_FIELDS)[number], string>>({
    room: initialDraft?.fieldAuthor['room'] ?? PREFILL_AUTHOR,
  });

  /**
   * ⭐ Board 2706 (plan D4) — REPORT THE UNSAVED WORK to the shell (`onDraftChange`). `null` when
   * pristine or while the sent panel is up (a sent request is not unsaved work). The authorship
   * ref is copied, never shared, so the held draft cannot move with a later keystroke.
   * ⛔ NO REPORT ON UNMOUNT: the lock unmounts this form, and that is exactly when the shell must
   * keep what was last reported.
   */
  useEffect(() => {
    if (onDraftChange === undefined) return;
    onDraftChange(
      success !== null || isPristineForm(values, residentQuery)
        ? null
        : { values, residentQuery, fieldAuthor: { ...fieldAuthor.current } },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, residentQuery, success]);

  /**
   * ⛔ EVERY NURSE-DRIVEN WRITE TO ONE OF THE THREE GOES THROUGH HERE, so the
   * author is recorded in the same act as the value. A bare `setField` on any of
   * them silently leaves the previous author in place, which is exactly how a
   * value ends up travelling to somebody it was not entered for.
   * ⚠️ The tap-to-fill button counts as an edit: she chose it, under whoever was
   * matched at the time.
   */
  function editPrefilledField(key: (typeof PREFILLED_FIELDS)[number], value: string): void {
    // ⛔ THROUGH `writeFields` LIKE EVERY OTHER WRITE — this is the path the
    // MEASURED instance of II-portal-#12 took. Room, next-of-kin name and
    // next-of-kin phone do NOT go through `setField`; they come through this
    // function so their authorship can be recorded. Fixing the clear in
    // `setField` alone would have left "Room is required." standing under a
    // field holding `12A`, which is the exact screen M2 measured.
    writeFields([key], (prev) => {
      fieldAuthor.current = { ...fieldAuthor.current, [key]: prev.residentId };
      return { ...prev, [key]: value };
    });
  }

  /**
   * ⭐⭐ T1717 M4 (DD3, P0-6) — PREFILL IS ONE FUNCTION, NOT TWO COPIES, AND IT
   * HANDLES THE MATCH **CHANGING**, NOT ONLY THE MATCH ARRIVING.
   *
   * ⛔ THE TWO ENTRY POINTS MUST NOT DRIFT. `onResidentInput` (typing the full
   * name) and `pickSuggestion` (tapping the suggestion) are the same act to a
   * nurse, and before M4 they were two hand-written copies of one line. Adding
   * two more fields to each would have made a divergence — prefill the pair on
   * one path and not the other — invisible in review and unfindable in use.
   *
   * ⛔⛔ THE OUTPUT GATE FOUND THE DEFECT THIS RULE EXISTS TO CLOSE, AND IT WROTE
   * A THIRD PARTY'S DETAILS ONTO THE WRONG PERSON, PERMANENTLY. The first cut
   * only ever ADDED: on a match it filled, and on a no-match it cleared
   * `residentId` alone. So typing a saved resident and then retyping a name
   * matching nobody left the FIRST resident's room and next-of-kin pair sitting
   * in the form — and `createTypedResident` then POSTed them as the NEW person's
   * own room and next of kin. That is a `portal.residents` row, DELETE is
   * withheld, and the erasure sequence works per `resident_id`: the wrong
   * person's contact details would have been attached to a second individual and
   * survived the erasure of the first.
   * ⛔ `A → B` HAD THE SAME HOLE FOR THE PAIR. Room was written unconditionally,
   * but the pair was written only when B HAD one — so booking for a resident
   * with no next of kin recorded carried the PREVIOUS resident's name and number
   * onto B's request row. Same misattribution, different door.
   *
   * ⛔ SO THE RULE IS PER FIELD AND IT IS ABOUT AUTHORSHIP, NOT ABOUT EMPTINESS.
   * A field still holding exactly what the prefill wrote is the FORM's value and
   * follows the match — replaced by B's, or cleared when nothing matches. A
   * field the nurse has changed is HERS and is never touched. An empty field
   * takes the incoming value, which is the ordinary first-prefill case.
   *
   * ⛔ A RESIDENT WITH NO PAIR STILL PREFILLS NOTHING (DD3), and now the "nothing"
   * is enforced in both directions: it does not merely decline to write, it
   * clears a stale value it owns, so the required fields SURFACE rather than
   * arriving pre-answered with somebody else's contact.
   *
   * ⚠️ WHAT THIS DELIBERATELY DOES NOT DO is erase what the nurse typed BEFORE
   * any resident matched — the snapshot is `null` then, so no field is form-owned
   * and every value is hers. That property has its own arm and stays green.
   */
  /**
   * ⭐⭐ R2 — THE ONE PLACE RESIDENT-SCOPED STATE CHANGES. **SIX PIECES**, counted
   * here so the next author sees the whole set rather than the two they came for:
   *
   *   1. `values.residentId`            — which resident this request is for
   *   2. `PREFILLED_FIELDS`             — the room (three of them before T1868 M6,
   *                                       which removed the next-of-kin pair)
   *   3. `fieldAuthor`                  — who authored each of those
   *   4. `values.updateRoster`          — the CONSENT, which is about a person
   *   5. `rosterUpdateNotice`           — an outcome reported for a particular row
   *   6. `removedOffer`                 — a resident id plus a button that acts on it
   *
   * The tint and the status line are DERIVED from 1 + 3 (`prefilledNow`), so they
   * follow for free and are deliberately not a seventh piece.
   *
   * ⛔ EVERY CALLER ROUTES THROUGH HERE — typing, the suggestion tap, the settle
   * on blur, the settle on submit, the create-from-request arrival and the DD10
   * put-back. Two separate reviews found a piece left behind when the resets sat
   * at the call sites (`updateRoster` and `rosterUpdateNotice` in one, the
   * suggestion path's `removedOffer` in the other); a seventh caller must not be
   * able to reproduce that, so there is nowhere else to write them.
   *
   * ⚠️ RETURNS the computed values, because callers in the same tick (submit)
   * cannot read them back out of state yet.
   */
  function applyResidentMatch(
    match: MetaResident | undefined,
    keepValues = false,
  ): RequestFormValues {
    const target = match ? String(match.id) : '';
    const authors = { ...fieldAuthor.current };
    const next: RequestFormValues = { ...values, residentId: target, updateRoster: false };

    if (keepValues) {
      /**
       * ⭐ THE CREATE / PUT-BACK ARRIVAL. The resident was just minted or
       * reactivated FROM WHAT SHE TYPED, so every one of the three fields was
       * authored under this very person. Nothing is replaced — there is nothing
       * on the roster to replace it with that she did not supply herself.
       */
      for (const key of PREFILLED_FIELDS) authors[key] = target;
    } else {
      const incoming: Record<(typeof PREFILLED_FIELDS)[number], string> = {
        room: match ? match.room : '',
      };
      for (const key of PREFILLED_FIELDS) {
        const author = authors[key];
        if (author === PREFILL_AUTHOR) {
          // The form wrote it, so the form takes it back: it follows the match,
          // to the new resident's value or to nothing.
          next[key] = incoming[key];
        } else if (author === '') {
          // Typed with NOBODY matched — she was describing the person she is
          // about to name. The named resident's own value wins if there is one;
          // otherwise hers stands, because it was never about somebody else.
          if (incoming[key] !== '') {
            next[key] = incoming[key];
            authors[key] = PREFILL_AUTHOR;
          }
        } else if (author !== target) {
          // ⛔⛔ TYPED UNDER A DIFFERENT RESIDENT. It is about THAT person and
          // does not travel — replaced by the new resident's value, or cleared
          // so the required field surfaces rather than carrying a stranger's.
          next[key] = incoming[key];
          authors[key] = PREFILL_AUTHOR;
        }
        // else: author === target — she typed it for THIS resident. Keep it.
      }
    }

    /**
     * ⛔ R3 — THE REF IS WRITTEN **OUTSIDE** ANY `setValues` UPDATER, and that is
     * a correctness rule rather than tidiness. Authorship lived inside a
     * functional updater until this batch; an updater may be invoked more than
     * once for one logical update (StrictMode, batching, a replayed render), and
     * a ref mutation inside it applies once per invocation. The second pass then
     * reads authors the first pass already rewrote and reaches a different
     * answer — a state machine that depends on how many times the renderer felt
     * like calling it. Here the whole transition is computed from the render's
     * own `values`, written once, and handed back to the caller.
     */
    fieldAuthor.current = authors;
    /**
     * ⛔⛔ THE FOURTH WRITE PATH, AND IT SETTLES WHAT IT WRITES (II-portal-#12,
     * found by the M4 output gate). This function fills Room and the next-of-kin
     * pair from the matched roster row without a keystroke ever reaching them, so
     * before this it left "Room is required." standing over an auto-filled `4B`
     * with `aria-invalid="true"` on the control.
     * ⛔ ONLY THE KEYS WHOSE VALUE ACTUALLY MOVED. A match that leaves a field
     * exactly as it was has not answered anything - a resident with no next of
     * kin "prefills" `''` over an already blank control, and clearing there would
     * take the required-field message off a field that is still empty and still
     * required. The comparison against this render's `values` is the test.
     * ⚠️ `setValues(next)` STAYS A WHOLE-OBJECT SET, not a functional update:
     * `submit` calls this twice in one tick and reads the RETURNED values, so the
     * committed state and that return value have to be the same computation.
     */
    const settled: ReadonlyArray<'residentId' | (typeof PREFILLED_FIELDS)[number]> = [
      'residentId',
      ...PREFILLED_FIELDS,
    ];
    writeFields(
      settled.filter((k) => next[k] !== values[k]),
      next,
    );
    setRosterUpdateNotice(null);
    setRemovedOffer(null);
    return next;
  }

  /**
   * ⭐ WHICH FIELDS ARE **STILL** SHOWING PREFILLED VALUES — the tint and the
   * status line, both DERIVED from the identity epoch rather than stored.
   *
   * ⛔ DERIVED, NEVER A FLAG OF ITS OWN. A separate "this was prefilled" boolean
   * would be a seventh piece of resident-scoped state to reset, and this
   * milestone's reviews found two that were missed when there were only three.
   * ⚠️ NON-EMPTY ONLY: a resident with no pair "prefills" `''`, and announcing
   * that as filled would walk her past two blank required controls.
   */
  function prefilledNow(): Array<(typeof PREFILLED_FIELDS)[number]> {
    if (values.residentId === '') return [];
    return PREFILLED_FIELDS.filter(
      (k) => fieldAuthor.current[k] === PREFILL_AUTHOR && values[k] !== '',
    );
  }

  /**
   * ⭐⭐ R1 — IDENTITY IS COMMITTED ON ARRIVAL AND LEFT ONLY ON A SETTLE.
   * PM ruling, 2026-09-03, after an advice consult on the live re-check.
   *
   * ⛔ THE BUG WAS THE EVENT MODEL, NOT THE RULE. This handler runs on EVERY
   * KEYSTROKE, so it used to re-decide identity on every keystroke — and while a
   * nurse is typing, "matches nobody" is the NORMAL state, not a decision. Delete
   * one character of a matched resident's name and the form concluded she had
   * left that resident: her edited room was cleared and refilled from the roster,
   * her consent was dropped, the tint and the status line flickered. Fixing a
   * typo destroyed her work.
   *
   * ⛔ SO THE TWO DIRECTIONS ARE ASYMMETRIC, DELIBERATELY:
   *   · ARRIVING is instant — an exact match, or a suggestion tap, commits.
   *   · LEAVING needs the no-match to SETTLE — blur of this field with the text
   *     matching nobody, or submit with it matching nobody (the create path).
   * Mid-typing with no match, EVERYTHING stays: the identity, the three values,
   * their authorship, the tint, the status line, the checkbox and its tick.
   *
   * ⚠️ AND A MATCH THAT IS ALREADY THE CURRENT IDENTITY IS NOT A TRANSITION. That
   * guard is what makes typing the rest of an already-matched name a no-op, and
   * it is what arm 7 (no flicker) actually measures.
   */
  function onResidentInput(text: string): void {
    setResidentQuery(text);
    /**
     * ⛔⛔ A KEYSTROKE CLEARS ONLY WHAT IT ANSWERS - T1723 M4d, security review
     * (LOW). This used to be a bare `writeFields(['residentId'])`: one
     * character took `'Your list is full.'`, `'Adding residents is not switched
     * on here yet.'` and `'That resident is not on your list any more.'` off
     * the screen, and the nurse read the silence as the problem being solved.
     * Three of those four messages are facts about the HOME, which no text can
     * answer; one is about the exact name she sent.
     * ⛔ SO THE TEST IS THE PROVENANCE, NOT THE EVENT. `null` means no refusal
     * was recorded through `refuseResident` - the pre-M4d behaviour stands, so
     * nothing this door does not know about regresses. A recorded TEXT clears
     * only once the box has MOVED away from it, by `sameResidentText`, which is
     * the store's own rule: a trailing space or a case change is not a move.
     * `NOT_ABOUT_TEXT` never clears here at all - those go when an identity is
     * PICKED (`applyResidentMatch` settles `residentId` through `writeFields`)
     * or on the next Send, which re-derives every message.
     */
    const about = residentRefusedAbout.current;
    if (about === null || (about !== NOT_ABOUT_TEXT && !sameResidentText(text, about))) {
      writeFields(['residentId']);
    }
    /**
     * ⛔ AND THE DD10 OFFER IS ABOUT THE NAME SHE SENT TOO. Under the R1 settle
     * model it survives typing, so without this it stayed on screen while she
     * typed somebody else's name - and one tap would then reactivate and book a
     * person the box no longer names.
     */
    if (removedOffer !== null && !sameResidentText(text, removedOffer.displayName)) {
      setRemovedOffer(null);
    }
    const match = matchResident(text);
    if (match && String(match.id) !== values.residentId) applyResidentMatch(match);
  }

  /**
   * ⛔ THE SETTLE. A no-match only becomes a DECISION when she stops typing this
   * field. Submit is the other settle, and it lives in `submit` because it has to
   * run BEFORE validation — see there.
   */
  function onResidentBlur(): void {
    if (values.residentId !== '' && !matchResident(residentQuery)) applyResidentMatch(undefined);
  }

  function pickSuggestion(r: MetaResident): void {
    // ⛔ THE BARE `displayName`, NEVER THE COMPOSED PICKER LABEL, AND THAT IS
    // A DD9 CONTROL RATHER THAN A PREFERENCE. `residentDisplayName` is one of
    // the keys that CROSSES THE KIOSK SEAM (`PORTAL_REQUEST_KEEP_KEYS` in
    // `quote-helper/src/core/portal-ops-projection.ts`); `room` is deliberately
    // dropped there. Since board 1861 the chip renders name AND room in one
    // string, so feeding that string back into this field would carry a room
    // across the seam inside a name. Pinned by the "selects BY ID" arm in
    // `test/client/request-form.spec.tsx`, which asserts this field holds
    // exactly the name.
    setResidentQuery(r.displayName);
    applyResidentMatch(r);
  }

  /**
   * ⭐ THE TYPEAHEAD'S MATCHES: the capped list the screen renders AND the total
   * it was capped from, computed in ONE place because the buttons, the visible
   * hint and the live region must never be able to disagree about them.
   *
   * ⛔ `total` IS TAKEN BEFORE THE SLICE - A2-newrequest-1, T1723 M4d security
   * review (LOW). The count said `6 matching names` over a list that had been
   * cut from twelve, so a nurse whose resident was seventh was told, precisely,
   * that there were six and hers was not among them. The cap stays (six is the
   * Helen-test budget); what changes is that the screen says it is capping.
   */
  const RESIDENT_SUGGESTION_CAP = 6;
  function residentMatches(): { shown: MetaResident[]; total: number } {
    const needle = residentQuery.trim().toLowerCase();
    if (needle.length === 0 || values.residentId !== '') return { shown: [], total: 0 };
    const all = (meta?.residents ?? []).filter((r) => r.displayName.toLowerCase().includes(needle));
    return { shown: all.slice(0, RESIDENT_SUGGESTION_CAP), total: all.length };
  }

  /** The sentence both the live region and the visible hint render, or `''` when
   *  there is nothing to say. ONE function, so the two cannot drift. */
  function residentMatchCount(): string {
    const { shown, total } = residentMatches();
    if (shown.length === 0) {
      /**
       * ⭐⭐ THE NO-MATCH CASE IS ANNOUNCED TOO (Captain's M4 delta 1). The
       * region carried a sentence for N matches and was left EMPTY for zero, so
       * the one keystroke that changes what the screen OFFERS (the tap targets
       * go, a creation promise takes their place) was the one a screen reader
       * heard nothing about. Silence there reads as "still looking", not as
       * "nobody".
       *
       * ⛔ A COUNT, NEVER THE NAME. The visible hint below names the person
       * ("New resident: Aoife will be added"); this region may not, because a
       * live region is READ ALOUD and the device is shared: a ward desk, a day
       * room, a corridor. A resident's name spoken into a room she may be
       * sitting in is a disclosure the screen itself never makes.
       *
       * ⛔ AND IT MUST NOT SAY "matching name". The arms that prove the count
       * is announced exactly once COUNT the status regions holding that phrase;
       * a no-match sentence carrying it would be counted as a match.
       *
       * ⚠️ `total === 0` is redundant today (`residentMatches` returns zero of
       * both or neither) and is stated anyway, because it is the rule this
       * branch MEANS: a future cap that returned an empty `shown` over a
       * non-zero `total` would otherwise announce "nobody" across a roster that
       * had matches.
       *
       * ⚠️ INLINE, not in `client/copy.ts`, for the same reason the count
       * above is: that file is fenced for this milestone. Folding both in is
       * the reported residual.
       */
      return residentQuery.trim().length > 0 && values.residentId === '' && total === 0
        ? 'Nobody on the list matches.'
        : '';
    }
    if (total > shown.length) {
      return `${String(shown.length)} of ${String(total)} matching names shown. Tap one, or keep typing.`;
    }
    return shown.length === 1
      ? '1 matching name. Tap it, or keep typing.'
      : `${String(shown.length)} matching names. Tap one, or keep typing.`;
  }

  /**
   * ⭐⭐ board 2732 M5 (plan DD3, R-4, R-5, R-7) — WHO IS REQUESTING, BY TYPING.
   * The resident box's suggestion pattern, NOT a combobox: a plain input, the
   * offered names as `.resident-suggestion` buttons, one polite live region for
   * the count, and an explicit `Add` for a missing name. ONE STATE per form: the
   * text (`requesterQuery`), the picked id (`requestedByStaffId`, the only one
   * sent) and a staged add (`requesterAdd`).
   * ⛔ THE TEXT BECOMES THE REQUESTER ONLY BY AN EXPLICIT ACT: a tapped
   * suggestion, a text matching exactly one offered name under the shared
   * `sameStoredName` rule (the resident box's auto-pick), or an `Add` tap. ANY
   * EDIT CLEARS ALL THREE, so a picked id can never survive a changed name (the
   * stale-id bug). No keydown handler: Enter submits the form as today and never
   * stages an add, so "Mar" + Enter is the required-field refusal, never a junk
   * name in a list the app cannot delete from.
   * ⛔ TWO DEVIATIONS FROM THE RESIDENT BOX, both recorded (plan DD3) and flagged
   * for the Vicky sitting: an add needs the explicit tap (a staff row cannot be
   * deleted by the app and counts toward the erasure ceiling), and the `Add`
   * chip also shows BESIDE partial matches when none is the same name, because
   * otherwise "Ann" could never be added while "Anne Kelly" exists.
   */
  /** The names this account may pick, narrowed by the chosen group for reception
   *  and admin; in wire order (recently used first), never re-sorted. */
  function offeredStaff(group: string): MetaStaff[] {
    const all = meta?.staff ?? [];
    return role === 'group' ? all : all.filter((s) => s.groupLabel.toLowerCase() === group.toLowerCase());
  }
  function requesterMatches(): { shown: MetaStaff[]; total: number } {
    const needle = values.requesterQuery.trim().toLowerCase();
    if (needle.length === 0 || values.requestedByStaffId !== '') return { shown: [], total: 0 };
    const all = offeredStaff(values.requesterGroup).filter((s) => s.displayName.toLowerCase().includes(needle));
    return { shown: all.slice(0, REQUESTER_SUGGESTION_CAP), total: all.length };
  }
  /** The ONE sentence the live region and the visible count both render. A
   *  count, never a name: the live region is read aloud on a shared device. */
  function requesterMatchCount({ shown, total }: { shown: MetaStaff[]; total: number }): string {
    if (values.requestedByStaffId !== '' || values.requesterQuery.trim().length === 0) return '';
    if (shown.length === 0) return requesterBoxCopy.noneFound;
    if (total > shown.length) return requesterBoxCopy.foundCapped(shown.length, total);
    return requesterBoxCopy.found(shown.length);
  }
  /** Is the `Add` chip on offer? Only when the server says this session can add,
   *  the cleaned text is non-empty, NO offered name is the same name (the shared
   *  `sameStoredName`, the client's rule; the server's `staffKey` is the backstop
   *  that answers `staff_exists` with the id for a compatibility variant), a
   *  reception / admin account has chosen a group, and nothing is picked or
   *  already staged. */
  function requesterAddOffered(): boolean {
    if (meta?.staffAddable !== true) return false;
    if (values.requestedByStaffId !== '' || values.requesterAdd) return false;
    if (role !== 'group' && values.requesterGroup === '') return false;
    if (cleanStoredText(values.requesterQuery) === null) return false;
    return !offeredStaff(values.requesterGroup).some((s) => sameStoredName(s.displayName, values.requesterQuery));
  }
  function onRequesterInput(text: string): void {
    writeFields(['requestedByStaffId'], (prev) => {
      const exact = offeredStaff(prev.requesterGroup).filter((s) => sameStoredName(s.displayName, text));
      return {
        ...prev,
        requesterQuery: text,
        requestedByStaffId: exact.length === 1 ? String(exact[0]!.staffId) : '',
        requesterAdd: false,
      };
    });
  }
  // ⭐ ux-review (M5 wave 1): the tapped chip unmounts, so focus goes back to the box rather
  // than falling to the page body; the box is still mounted when the handler runs.
  function pickRequesterSuggestion(s: MetaStaff): void {
    writeFields(['requestedByStaffId'], (prev) => ({
      ...prev,
      requesterQuery: s.displayName,
      requestedByStaffId: String(s.staffId),
      requesterAdd: false,
    }));
    document.getElementById('rf-requester')?.focus();
  }
  function stageRequesterAdd(): void {
    writeFields(['requestedByStaffId'], (prev) => ({ ...prev, requesterAdd: true }));
    document.getElementById('rf-requester')?.focus();
  }

  /**
   * ⭐⭐ board 2732 M5 (plan DD4) — WRITE THE STAGED NAME, at Send, BEFORE any
   * resident or request write. `createTypedResident`'s shape: 201 picks the new
   * id and RE-READS meta, so the name is in the list the box offers; 409
   * `staff_exists` picks the id the server returns (a compatibility variant of an
   * existing name lands here, the `staffKey` backstop); every refusal is a field
   * error under the box and stops the send, so a refused staff add never
   * precedes an Article 9 resident write. Returns the picked staff, or null.
   */
  async function createTypedRequester(
    from: RequestFormValues,
  ): Promise<{ staffId: number; displayName: string } | null> {
    const displayName = from.requesterQuery.trim();
    const res = await api.post<{
      staff?: { staffId: number; displayName: string; groupLabel: string };
      error?: string;
      staffId?: number;
    }>('/api/staff', role === 'group' ? { displayName } : { displayName, group: from.requesterGroup });
    // ⭐ M5 wave 1 (ux-review, plan-alignment): a refusal UN-STAGES the add, so the "will be
    // added" hint never sits under the sentence saying it will not be; the chip is offered again.
    const refuse = (message: string): null => {
      const errs = { requestedByStaffId: message };
      setErrors((prev) => ({ ...prev, ...errs }));
      setValues((prev) => ({ ...prev, requesterAdd: false }));
      focusFirstError(errs);
      return null;
    };
    if (res.kind === 'network-error') {
      setSubmitError(sharedCopy.networkError);
      return null;
    }
    if (res.status === 201 && res.body?.staff && Number.isSafeInteger(res.body.staff.staffId)) {
      await loadMeta();
      return { staffId: res.body.staff.staffId, displayName: res.body.staff.displayName };
    }
    const code = res.body?.error;
    if (res.status === 409 && code === 'staff_exists' && Number.isSafeInteger(res.body?.staffId)) {
      const existing = offeredStaff(from.requesterGroup).find((s) => s.staffId === res.body?.staffId);
      return { staffId: res.body!.staffId!, displayName: existing?.displayName ?? displayName };
    }
    // An admin is never told to ask its home's admin (R-8's rule, applied to the refusals too).
    if (code === 'staff_deactivated') {
      return refuse(role === 'admin' ? requesterBoxCopy.deactivatedAdmin : requesterBoxCopy.deactivated);
    }
    if (code === 'staff_ceiling') return refuse(role === 'admin' ? requesterBoxCopy.ceilingAdmin : requesterBoxCopy.ceiling);
    if (res.status === 429) return refuse(requesterBoxCopy.throttled);
    if (code === 'group_forbidden' || code === 'group_invalid') return refuse(requesterBoxCopy.groupRefused);
    if (code === 'group_unset') return refuse(requesterBoxCopy.groupUnset);
    if (res.status === 400) return refuse(requesterBoxCopy.nameRefused);
    return refuse(sharedCopy.genericError);
  }

  /**
   * ⭐ P1-2 — THE ROSTER ROW THE FORM IS CURRENTLY EDITING A COPY OF, or null.
   *
   * ⛔ LOOKED UP BY THE SELECTED ID, never by the typed text. A resident created
   * in this very submit is on the roster by the time the checkbox could matter
   * (`createTypedResident` re-reads meta), and a name still being typed selects
   * nobody — so the id is the only key that means "an EXISTING resident whose
   * standing record could be rewritten".
   */
  function selectedRosterResident(v: RequestFormValues): MetaResident | undefined {
    if (v.residentId === '') return undefined;
    return meta?.residents.find((r) => String(r.id) === v.residentId);
  }

  /**
   * ⛔ THE CONTROL APPEARS ONLY WHEN A PREFILLED VALUE WAS CHANGED (P1-2). Not
   * "whenever a resident is selected": a checkbox offering to save what is
   * already saved is a control with no effect, and a nurse who taps it once out
   * of habit learns to tap it always — which is how the silent write-back DD3
   * forbids arrives by the front door.
   *
   * ⚠️ COMPARED TRIMMED, AND AN ABSENT ROSTER VALUE COMPARES AS `''`. "Not
   * recorded" versus "typed for this journey" IS a difference and must offer the
   * control — that is how a nurse gets a room onto the roster for the first time
   * from the form she is already on. ⛔ `portal.residents.room` is `TEXT NOT NULL`
   * with NO non-blank CHECK, so a resident whose room has never been recorded is
   * a row the database really permits; this is not a hypothetical branch.
   */
  function rosterDiffers(v: RequestFormValues): boolean {
    const roster = selectedRosterResident(v);
    if (!roster) return false;
    // ⚠ THE `?? ''` DEFENDS THE WIRE, NOT THE TYPE. `MetaResident.room` is
    // declared non-null, but `meta` is a network response and a declaration is
    // not a guarantee.
    return (roster.room ?? '').trim() !== v.room.trim();
  }

  /**
   * Creates the typed-but-unknown resident right before submit, so the nurse
   * never leaves the flow. Returns the new resident's id, or null after
   * surfacing a field error. Room comes from the one Room field and becomes both
   * their roster room and this booking's room.
   *
   * ⚠️ T1717 M4 (P0-13) ADDED THE NEXT-OF-KIN PAIR TO THIS BODY AND T1868 M6
   * REMOVED IT AGAIN, so the body is `{ displayName, room }`. The docblock that
   * stood here described the pair's arrival — a resident created from a request
   * used to land on the roster with no contact at all, so the nurse retyped it
   * every journey — and it is corrected rather than struck, because the SHAPE it
   * warned about survives: this is a `portal.residents` INSERT, `roles.sql`
   * withholds DELETE, and anything wrong that reaches it cannot be taken back.
   *
   * ⛔ BOTH SURVIVING FIELDS ARE REQUIRED, so the old "sent raw and may be blank"
   * rule has no field left to apply to. `validateResidentFields` refuses an
   * absent or blank `displayName` or `room` with a 400 rather than storing NULL,
   * and the room guard below is what stops this function ever reaching it blank.
   */
  async function createTypedResident(
    from: RequestFormValues,
  ): Promise<{ id: number; displayName: string } | null> {
    const displayName = residentQuery.trim();
    const room = from.room.trim();
    /**
     * ⚠️ NO LONGER REACHABLE FROM THIS FORM, AND KEPT ANYWAY. `submit` validates
     * every non-identity field before it calls this, and `validateRequestInput`
     * requires `room` — so a blank one is refused there with its own message and
     * this branch never fires. It stays as a CONTRACT guard on the function: it
     * is the only thing stopping a future caller POSTing a blank room to an
     * Article 9 write. ⛔ It is not the user-facing path any more; do not test it
     * as though it were.
     */
    if (room.length === 0) {
      setErrors((prev) => ({ ...prev, room: requestFormCopy.addResidentRoomRequired }));
      return null;
    }
    const res = await api.post<{
      resident?: { id: number; displayName: string; room: string };
      error?: string;
      residentId?: number;
      errors?: Record<string, string>;
    }>('/api/residents', {
      displayName,
      room,
    });

    if (res.kind === 'network-error') {
      setSubmitError(sharedCopy.networkError);
      return null;
    }
    if (res.status === 403) {
      // ⛔ A CAPABILITY, NOT A NAME. Adding residents is switched off for this
      // home; no text she can type answers that, so typing must not clear it.
      refuseResident(requestFormCopy.addResidentReadOnly, NOT_ABOUT_TEXT);
      return null;
    }
    /**
     * ⭐⭐ THE THREE 409s, ANSWERED IN THE ROSTER TAB'S OWN WORDS (DD7, DD10).
     *
     * ⛔ THEY ARE NOT "an error" — each names a state the nurse can act on, and
     * before M4 all three fell through to the generic branch below and rendered
     * whatever `errors` happened to hold, which for a body carrying `error` and
     * no `errors` was the bare `sharedCopy.genericError`. She was told "Something
     * went wrong" about a resident who is simply already on her list.
     *
     * ⛔ `resident_removed` IS THE ONE THAT MATTERS AND IT IS NOT AN ERROR AT
     * ALL. It is DD7's mechanism for one-person-one-row: the name was removed,
     * the row still exists with its history and its next-of-kin data, and minting
     * a second row would put a second copy of somebody's Article 9-adjacent
     * contact under a second id that the erasure sequence (runbook §4a, which
     * erases ONE `resident_id`) would then miss. So it becomes an OFFER, not a
     * refusal — the same offer the roster tab makes, from the same copy.
     */
    if (res.status === 409) {
      const code = res.body?.error;
      // ⚠️ A POSITIVE INTEGER, not merely `typeof 'number'` — `NaN`, `1.5` and
      // `-1` all pass that test and `putResidentBack` interpolates the value
      // into `/api/residents/${id}/active`.
      // ⛔ THE PREDICATE IS `api.ts`'s `isResidentId`, SHARED — T1717 M7,
      // security review. It was inlined here at the deltas sweep with a note
      // saying "if a third appears, extract it"; a third had already appeared
      // (the roster's own 409 handler), and the three did not agree. One
      // predicate now, so they cannot.
      if (code === 'resident_removed' && isResidentId(res.body?.residentId)) {
        setRemovedOffer({ residentId: res.body.residentId, displayName });
        return null;
      }
      /**
       * ⛔ AND THE THREE ANSWER TO DIFFERENT THINGS, which is why the provenance
       * is stated per branch rather than once for the block. `resident_exists`
       * is about the exact NAME she sent - different text is a real answer to
       * it. `resident_limit` is about the HOME's list being full, and the
       * fallback is about a server that refused for a reason this screen cannot
       * name; neither is answered by any keystroke.
       */
      if (code === 'resident_exists') {
        refuseResident(myResidentsCopy.alreadyOnList(displayName), displayName);
      } else if (code === 'resident_limit') {
        refuseResident(myResidentsCopy.listFull, NOT_ABOUT_TEXT);
      } else {
        refuseResident(
          Object.values(res.body?.errors ?? {})[0] ?? sharedCopy.genericError,
          NOT_ABOUT_TEXT,
        );
      }
      return null;
    }
    if (res.status !== 201 || !res.body.resident) {
      // The server refused for a reason this screen cannot name, so nothing she
      // types is known to answer it.
      refuseResident(
        Object.values(res.body?.errors ?? {})[0] ?? sharedCopy.genericError,
        NOT_ABOUT_TEXT,
      );
      return null;
    }
    // Re-read meta rather than splicing the new row in locally, so the
    // roster stays exactly what the server says it is.
    await loadMeta();
    return res.body.resident;
  }

  /**
   * ⭐ DD10 — "PUT THEM BACK", THEN CARRY ON WITH THE SAME SEND.
   *
   * ⛔ IT REACTIVATES AND CONTINUES, rather than reactivating and asking her to
   * press Send again. She already pressed Send; the row's absence is the
   * system's bookkeeping, not a decision she has to re-take. Two taps for one
   * intention is how a nurse concludes the form did not work.
   *
   * ⛔ AND IT CONTINUES WITH **THAT** ID, never with a fresh create — that is the
   * whole point of the 409 carrying the id (DD7): one person, one row, one
   * `resident_id` for the erasure sequence to reach.
   *
   * ⚠️ A 403 HERE IS THE SAME SHUT CAPABILITY as everywhere else on this path,
   * so it renders the roster tab's own refusal string rather than a fourth
   * wording of one fact.
   */
  async function putResidentBack(): Promise<void> {
    if (!removedOffer || submitting || sendPending) return;
    const offer = removedOffer;
    // ⛔ Board 2706 rework (round 3): THE SERVER'S CLOCK AND THE HOME'S OFFSET ARE RE-READ FIRST,
    // exactly as `submit` re-reads them before a new resident's probe. The collection bound below
    // depends on both, and judged with the page-load offset (or skipped, as round 2 did) a
    // changed appointment time, the clock passing or an operator raising the offset let the
    // reactivation go through for a request that was then refused as too soon. A READ, so nothing
    // is begun for it; a failed re-read stops here, nothing written.
    const pressedBy = accountRef.current;
    setSubmitting(true);
    const fresh = await api.get<MetaResponse>('/api/meta');
    setSubmitting(false);
    if (fresh.kind === 'network-error') {
      setSubmitError(sharedCopy.networkError);
      return;
    }
    if (fresh.status !== 200 || parseServerNow(fresh.body?.serverNow) === null) {
      setSubmitError(sharedCopy.genericError);
      return;
    }
    if (!mountedRef.current || accountRef.current !== pressedBy) return;
    recordServerClock(fresh.body.serverNow);
    const freshOffsetMinutes = fresh.body.home?.pickupOffsetMinutes ?? null;
    // ⛔ Board 2706 rework (R2, rounds 2 and 3): THE SEND'S OWN CHECKS FIRST, BEFORE THE
    // REACTIVATION. `sendRequest` refuses an unticked Cash / card, a blanked date or a collection
    // that is too soon, but only AFTER the POST below has put the resident back on the roster, so a
    // refused send still changed the standing record. The same lower-bound validation `sendRequest`
    // runs, with the offer's resident id and the offset just read, plus the same tick helper;
    // refused and focused the same way. `sendRequest` keeps its own check, with the same offset.
    // ⛔ ALL FIVE BOUNDS HERE, not only the lower ones (security re-check round 3): the clock was
    // re-anchored by the read just above, so an upper bound can only wrongly refuse a journey
    // within that round trip of the 28-day edge, the same reasoning as `submit`'s new-resident
    // probe; lower bounds only let a date past four weeks put the resident back for a request the
    // server then refused.
    const preCheck = validateRequestInput(
      assembleRequestPayload({ ...values, residentId: String(offer.residentId) }),
      { now: estimatedServerNow(), lowerBoundsOnly: false, pickupOffsetMinutes: freshOffsetMinutes },
    );
    const preErrors: Record<string, string> = { ...(preCheck.ok ? {} : preCheck.errors), ...payerConfirmErrors(values) };
    if (Object.keys(preErrors).length > 0) {
      setErrors(preErrors);
      recordWholesaleResidentRefusal(preErrors);
      focusFirstError(preErrors);
      return;
    }
    // ⭐ Board 2706 rework (R1, round 2): the send begins HERE, before its first write.
    const run = beginSend(draftNow(values, residentQuery));
    /**
     * ⛔⛔ `submitting` IS HELD FOR THE WHOLE SEQUENCE — reactivate, `loadMeta`
     * AND `sendRequest` — IN A `try/finally`. T1717 M7, Captain deltas #34.
     *
     * ⛔ WHAT THE DEFECT WAS. `setSubmitting(false)` sat immediately after the
     * reactivate POST, and `sendRequest` re-arms it only after its own
     * validation. Between the two there are TWO awaits (`loadMeta`, then the
     * assemble/validate) and `removedOffer` is still set — it is cleared by the
     * R2 epoch further down. So the guard on line 1 of this function was open
     * for a whole round trip: a second tap on "Put them back", or on Send,
     * issued a SECOND reactivate and a SECOND `POST /api/requests`. Request
     * creation carries no idempotency key, so that is two real bookings for one
     * intention — the exact double-booking a nurse produces when a screen looks
     * unresponsive for a second.
     * ⚠️ `finally`, not a trailing call: every branch below returns early, and
     * `sendRequest` can throw. `sendRequest` sets and clears `submitting`
     * itself; this outer clear is idempotent and simply outlives it.
     * ⭐ Arm: `request-form-prefill.spec.tsx` — "⛔ holds BOTH buttons through the
     * reactivate round trip - no second reactivate, no second request". ⚠️ The
     * file name here was wrong until T1717 M7's security review caught it (it
     * said `request-form.spec.tsx`, which holds no such arm): a citation to a
     * file that does not carry the test reads as coverage and is worth less than
     * no citation at all, because it stops the next reader looking.
     */
    setSubmitting(true);
    try {
      const res = await api.post<{ resident?: { id: number } }>(
        `/api/residents/${String(offer.residentId)}/active`,
        { active: true },
      );
      if (res.kind === 'network-error') {
        failSend(run);
        setSubmitError(sharedCopy.networkError);
        return;
      }
      if (res.status === 403) {
        failSend(run);
        // Both of these are facts about the ROSTER, not about the box: writes
        // are shut, or that row is gone. Typing cannot answer either.
        refuseResident(myResidentsCopy.writesClosed, NOT_ABOUT_TEXT);
        setRemovedOffer(null);
        return;
      }
      if (res.status !== 200) {
        failSend(run);
        refuseResident(myResidentsCopy.goneFromList, NOT_ABOUT_TEXT);
        setRemovedOffer(null);
        return;
      }
      // ⭐ Board 2706 rework (R1, round 2): the resident IS back, so the held copy names her now,
      // and a send that stops below hands back a draft that does not put her back a second time.
      const reactivated = { id: offer.residentId, displayName: offer.displayName, room: values.room };
      if (!stillMine(run)) {
        moveSend(run, draftNow(applyResidentMatch(reactivated, true), offer.displayName));
        failSend(run);
        return;
      }
      // The roster again, now holding the reactivated resident, so a later re-send re-matches her.
      await loadMeta();
      // ⛔ R2 — THROUGH THE EPOCH. It is an ARRIVAL at a reactivated resident, and
      // `keepValues` records that the three fields were authored under her: they
      // are what the nurse typed for this person, not roster values.
      // (`removedOffer` is cleared by the epoch itself.)
      const continued = applyResidentMatch(reactivated, true);
      moveSend(run, draftNow(continued, offer.displayName));
      /**
       * ⛔ THE NAME IS PASSED, NOT LOOKED UP — the live re-check found the success
       * line reading "This request for  to <destination>" with an empty name on
       * this path. `sendRequest` falls back to finding the resident in `meta`, and
       * `meta` here is the CLOSURE's value: `loadMeta()` above has resolved, but
       * the `setMeta` it performed has not re-rendered this function's scope, so
       * the reactivated resident is not in the list it searches.
       * ⛔ AND THE LOOKUP IS THE WRONG SOURCE ANYWAY. `offer.displayName` is the
       * name the nurse typed and the name the request was booked under — the same
       * value every other path already passes as `createdName`. A roster lookup can
       * only ever agree with it or be stale.
       */
      // ⭐ Board 2647 (P2): A FRESH OFFSET, never the closure's page-load value, so an
      // operator LOWERING the home's offset meanwhile cannot make this send refuse a
      // collection the server would accept. ⭐ Board 2706 rework (round 3): the one read
      // BEFORE the reactivation, so this check and the pre-check above agree.
      await sendRequest(continued, offer.displayName, freshOffsetMinutes, run);
    } finally {
      setSubmitting(false);
    }
  }

  function toggleEquipment(option: string, checked: boolean): void {
    // ⚠️ `errors['equipment']` is only reachable from a hand-built POST (these
    // checkboxes can only produce enum members), so the clear is a no-op today.
    // Routed anyway: the rule is "a write settles its own message", and an
    // exception here is how the fourth write path came to be missed.
    writeFields(['equipment'], (prev) => ({
      ...prev,
      equipment: checked ? [...prev.equipment, option] : prev.equipment.filter((e) => e !== option),
    }));
  }

  /**
   * ⭐⭐ P1-2 — THE ROSTER WRITE-BACK, AND IT IS THE ONLY ONE ON THIS SCREEN.
   *
   * ⛔ IT NEVER ABORTS THE SEND. The request is what the nurse came to do; the
   * roster update is a second, optional act she opted into. A refusal on the
   * optional half that swallowed the essential half would be a strictly worse
   * failure than not offering the control at all — so every non-200 sets a
   * notice and returns, and the caller carries straight on.
   *
   * ⛔ THE WHOLE ROW IS SENT, INCLUDING `displayName` — REQUIRED BY THE ROUTE,
   * AND TAKEN FROM THE ROSTER, NEVER FROM THIS FORM. `POST /api/residents/:id`
   * replaces the row (`validateResidentFields` refuses an absent `displayName`
   * with a 400), and the request form has no control that edits a resident's
   * NAME — the Resident field selects, it does not rename. Sending the roster's
   * own value is what keeps "an edited prefilled value" meaning the ROOM only (it
   * meant room and next of kin until T1868 M6); sending `residentQuery` would let
   * a typo in a search box silently rename a resident.
   */
  async function writeBackToRoster(v: RequestFormValues): Promise<void> {
    const roster = selectedRosterResident(v);
    if (!roster) return;
    const res = await api.post<{ resident?: unknown }>(`/api/residents/${v.residentId}`, {
      displayName: roster.displayName,
      room: v.room.trim(),
    });
    if (res.kind === 'network-error') {
      setRosterUpdateNotice(sharedCopy.networkError);
      return;
    }
    if (res.status === 403) {
      setRosterUpdateNotice(myResidentsCopy.writesClosed);
      return;
    }
    if (res.status !== 200) {
      setRosterUpdateNotice(sharedCopy.genericError);
      return;
    }
    setRosterUpdateNotice(null);
  }

  /**
   * ⭐⭐ Board 2706 rework (R1, round 2) — ONE SEND, FROM THE PRESS TO ITS ANSWER: what the shell
   * holds for it and whether it may still write.
   * ⛔ `sending` IS REPORTED BEFORE THE FIRST WRITE, NOT THE LAST. Reported only beside the
   * request POST, a lock that landed while a resident was being created or put back left a plain
   * draft held: the unlock offered it as sendable while the stalled chain went on to post it, so
   * one press could become two bookings.
   * ⛔ AND THE CHAIN NEVER STARTS A WRITE FOR SOMEBODY WHO IS GONE (`stillMine`): once the instance
   * that pressed Send has unmounted, or another account is signed in, the next write (the resident,
   * the put-back, the roster write-back, the request) is not made. The send settles `failed` with
   * the draft as it stands, a resident already written included, so the SAME account gets it back
   * sendable and nobody else posts it. A write already in flight is not recalled: its answer
   * settles the send as usual.
   */
  type SendRun = { draft: RequestFormDraft; account: number | undefined };
  function draftNow(v: RequestFormValues, query: string): RequestFormDraft {
    return { values: v, residentQuery: query, fieldAuthor: { ...fieldAuthor.current } };
  }
  function beginSend(draft: RequestFormDraft): SendRun {
    onSendPhase?.({ kind: 'sending', draft });
    return { draft, account: accountRef.current };
  }
  function moveSend(run: SendRun, draft: RequestFormDraft): void {
    onSendPhase?.({ kind: 'sending', draft, replaces: run.draft });
    run.draft = draft;
  }
  function stillMine(run: SendRun): boolean {
    return mountedRef.current && accountRef.current === run.account;
  }
  function failSend(run: SendRun): void {
    onSendPhase?.({ kind: 'failed', of: run.draft });
  }

  /**
   * ⛔ EXTRACTED FROM `submit` — T1717 M4, because DD10's "put them back" has to
   * CONTINUE the send the 409 interrupted, from a button rather than from a form
   * event. A second copy of the validate-then-POST sequence for that path is how
   * one of the two ends up missing the focus walk or the alert-failure panel.
   * `values` is passed in rather than read from state: both callers have just
   * computed a corrected copy, and `setValues` has not landed yet.
   */
  async function sendRequest(
    effectiveValues: RequestFormValues,
    createdName: string | null = null,
    // ⭐ Board 2647 (P2): the offset to validate with. A caller that has just
    // re-read `/api/meta` passes what it read; the page-load value otherwise.
    offsetMinutes: number | null = pickupOffsetMinutes,
    // ⭐ Board 2706 rework (R1, round 2): the send this belongs to, begun by the caller before
    // its own first write (`beginSend`). Every early return below settles it.
    run: SendRun,
  ): Promise<void> {
    const payload = assembleRequestPayload(effectiveValues);
    // ⚠️ THIS CALL USED TO CARRY A SECOND OPTION, `syntheticPhones`, and T1868 M6
    // deleted it with the next-of-kin phone it decided. The LESSON it carried is
    // kept because it is about how options are passed, not about phones: a
    // server-decided posture must be READ FROM THE SERVER, never hard-coded here.
    // Hard-coding `true` made the field unusable in real mode — this validator
    // refused a value the permissive server would have accepted, before the
    // server ever saw it.
    // ⭐ Board 2638 (plan D3): the same lesson for the CLOCK. `now` is the
    // estimate of the server's clock, never `new Date()`, and only the lower
    // bounds apply here, so a fast or slow device cannot refuse a valid request.
    const result = validateRequestInput(payload, {
      now: estimatedServerNow(),
      lowerBoundsOnly: true,
      pickupOffsetMinutes: offsetMinutes,
    });
    // ⛔ THE DOUBLE-CHECK IS MERGED INTO THE VALIDATOR'S OWN REFUSAL, NOT CHECKED EARLIER
    // (board 2186). `setErrors({})` below wipes the map wholesale, so a tick error raised
    // anywhere before this point would be cleared and the send would go through; and
    // `focusFirstError` only walks keys PRESENT in the map, so an unmerged error focuses nothing.
    // It must also land here, ABOVE the roster write-back further down, which persists even when
    // the request is then refused.
    // ⚠️ It is NOT in `validateRequestInput`: D3 stores nothing and the tick is not part of the
    // payload, so the shared validator has no field to judge. This is the one form-only rule.
    // ⭐ KEPT even though the probe in `submit` already refuses this: `putBackResident` (the DD10
    // "put them back" continuation) re-enters HERE without re-running the probe, so this is the
    // only gate on that path. Both sites call the one helper so they cannot drift.
    const confirmErrors = payerConfirmErrors(effectiveValues);
    if (!result.ok || Object.keys(confirmErrors).length > 0) {
      const merged = { ...(result.ok ? {} : result.errors), ...confirmErrors };
      setErrors(merged);
      // ⛔ THE THIRD WHOLESALE WRITE, RECORDED FOR THE SAME REASON AS THE
      // OTHER TWO. Unreachable with a resident error today (callers arrive with
      // a validated id), so this is the invariant holding by construction.
      recordWholesaleResidentRefusal(merged);
      focusFirstError(merged);
      failSend(run);
      return;
    }
    // ⛔ Board 2706 rework (R1, round 2): no write for somebody who is gone (see `SendRun`).
    if (!stillMine(run)) {
      failSend(run);
      return;
    }
    setErrors({});
    setSubmitting(true);
    // ⛔ BEFORE THE REQUEST, NOT AFTER (P1-2, and the plan says so in terms). The
    // roster is the standing record; if the two calls disagree, the one that
    // reflects what she typed must already be in place when Helen looks the
    // resident up. Guarded by `rosterDiffers` a second time so a stale tick —
    // ticked, then the value edited back to the roster's own — writes nothing.
    //
    // ⛔ AND STATE THE CONSEQUENCE, because it is the ruling and not an accident
    // (T1717 M7, Captain deltas #35): THE WRITE-BACK PERSISTS EVEN IF THE
    // REQUEST IS THEN REFUSED. `POST /api/residents/:id` has already committed
    // to `portal.residents` when the `POST /api/requests` below 400s, so a nurse
    // who corrects a room and then trips a validation error keeps the correction. That is P1-2 as ruled — a rectification is not
    // contingent on a booking — and it is the reason the order is this way round
    // rather than the reverse.
    // ⚠️ A successful write-back does NOT refresh `meta`, so on a re-send after a
    // refused request `rosterDiffers` is still true and the write-back fires
    // again. Harmless: it is a full-row REPLACE of the same row with the same
    // values, never a create, pinned by request-form-prefill.spec.tsx's "a
    // re-send after a refused request repeats the write-back idempotently" arm
    // (deltas #33). ⚠️ This citation named request-form.spec.tsx until the T1717
    // rework round; the arm never lived there.
    if (effectiveValues.updateRoster && rosterDiffers(effectiveValues)) {
      await writeBackToRoster(effectiveValues);
      if (!stillMine(run)) {
        setSubmitting(false);
        failSend(run);
        return;
      }
    }
    const res = await api.post<{
      requestId?: number;
      /** ⭐ T1683 M3 (A1) - the opaque job reference the server minted for this
       *  request. OPTIONAL on the wire for the same reason `alertFailed` is: an
       *  absent key must read as "none", never as `undefined` on screen. */
      jobRef?: string;
      /** ⛔ POSITIVE-FAILURE, so an ABSENT key means "no notice". A truthy
       *  negation of a delivered-flag would fire the failure panel against any
       *  server, stub or older build that does not send the key. */
      alertFailed?: boolean;
      errors?: Record<string, string>;
      // ⛔ Board 2638: THE TYPED PAYLOAD, NOT `result.value`. The value carries
      // instants composed against this browser's clock estimate; the wire carries
      // the Irish day and times, and the server composes and derives itself.
    }>('/api/requests', payload);
    setSubmitting(false);

    if (res.kind === 'network-error') {
      failSend(run);
      setSubmitError(sharedCopy.networkError);
      return;
    }
    if (res.status !== 201) failSend(run);
    // ⛔ Board 2706 rework (round 2): THE BODY MAY BE UNREADABLE (empty, or not JSON: `api.ts`
    // hands back `undefined`), and ANY 201 is a booking the server made. Reading a field off
    // `undefined` threw here, after `sending`, leaving the send stuck: Send disabled after a lock,
    // or Send enabled with no panel, which invites the second booking. Every field is null-safe and
    // the panel already allows `requestId` 0 and `jobRef` null.
    const body = (res.body ?? {}) as Partial<typeof res.body>;
    if (res.status === 201) {
      const residentName =
        createdName ?? meta?.residents.find((r) => r.id === result.value.residentId)?.displayName ?? '';
      // A free-typed destination (P5) has no meta row to look up; the typed
      // text itself is the name.
      const destinationName =
        result.value.destinationId !== null
          ? meta?.destinations.find((d) => d.id === result.value.destinationId)?.name ?? ''
          : result.value.destinationText ?? '';
      const sent: RequestSentFacts = {
        residentName,
        destinationName,
        requestId: typeof body.requestId === 'number' ? body.requestId : 0,
        jobRef: typeof body.jobRef === 'string' && body.jobRef !== '' ? body.jobRef : null,
        alertFailed: body.alertFailed === true,
      };
      onSendPhase?.({ kind: 'sent', of: run.draft, sent });
      setSuccess(sent);
      return;
    }
    if (res.status === 400 && body.errors) {
      setErrors(body.errors);
      // ⛔ A WHOLESALE SET, so the provenance is stated here rather than in
      // `refuseResident`: the shared validator's `'Select a resident.'` is
      // about the TEXT in the box at this moment (normally blank), which is the
      // one resident message a keystroke really does answer.
      recordWholesaleResidentRefusal(body.errors);
      // ⛔ THE SERVER'S REFUSAL GETS THE SAME TREATMENT AS THE CLIENT'S. A nurse
      // cannot tell which layer refused her, and the one that travels the
      // network is the one she is MOST likely to have scrolled away from while
      // waiting. Handling only the client branch is how "we added focus
      // management" becomes true of the easy half.
      focusFirstError(body.errors);
      return;
    }
    setSubmitError(sharedCopy.genericError);
  }

  async function submit(e: JSX.TargetedEvent<HTMLFormElement, Event>): Promise<void> {
    e.preventDefault();
    if (submitting || sendPending) return;
    setSubmitError(null);
    setRosterUpdateNotice(null);

    /**
     * ⛔⛔ R1 — SUBMIT IS A SETTLE, AND IT RUNS **BEFORE** VALIDATION.
     *
     * While she was typing, a text matching nobody was not a decision (see
     * `onResidentInput`). Pressing Send is the moment it becomes one. The
     * de-match therefore happens HERE, ahead of `validateRequestInput`, so a
     * field cleared by it SURFACES as a required-field error instead of a value
     * typed for the previous resident being sent for this one.
     * ⚠️ `applyResidentMatch` RETURNS the computed values because `setValues` has
     * not landed yet at this point in the same tick — reading `values` again here
     * would read the pre-settle state, which is the whole bug in miniature.
     */
    let effectiveValues = values;
    if (values.residentId !== '' && !matchResident(residentQuery)) {
      effectiveValues = applyResidentMatch(undefined);
    }

    const isNewResident =
      effectiveValues.residentId === '' && residentQuery.trim().length > 0;
    /**
     * ⭐⭐ board 2732 M5 (plan DD4) — A STAGED REQUESTER IS THE SECOND PENDING
     * CREATION, gated exactly like a new resident (memory 2638): the meta re-read,
     * the re-anchored clock and offset, and the STRICT probe all run when EITHER
     * is pending, because a staff row cannot be deleted by the app either.
     */
    const isNewRequester = effectiveValues.requesterAdd && effectiveValues.requestedByStaffId === '';
    const pendingCreation = isNewResident || isNewRequester;

    /**
     * ⛔⛔ NOTHING IS WRITTEN UNTIL THE WHOLE FORM IS VALID — the live re-check's
     * PHANTOM RESIDENT, and it is an Article 9 row that cannot be deleted.
     *
     * MEASURED: type a name nobody has, fill Room and the next-of-kin pair, leave
     * "Where to", the payer and the time blank, press Send. `POST /api/residents`
     * answered 201 — a real roster row carrying a third party's name and phone —
     * and only THEN was the form refused for the other fields. A nurse who gives
     * up at that point has left a resident behind with no journey, and
     * `roles.sql` withholds DELETE, so it is there for good.
     *
     * ⛔ DD7's ORPHAN POLICY DOES NOT COVER THIS, and the distinction is the whole
     * ruling. DD7 accepts an orphan when the REQUEST fails at the SERVER after the
     * resident exists — that is the job-reference retry, and the two-call shape is
     * what makes the retry possible. Client-side incompleteness is a different
     * thing entirely: nothing was attempted, nothing failed, and the write was
     * simply premature.
     *
     * ⛔ SO ONLY THE ORDER MOVES; THE TWO CALLS (DD7) STAY. Everything except the
     * resident's identity is validated FIRST, against a placeholder id that
     * satisfies `isPositiveInt` and NEVER reaches the wire — the real send below
     * re-validates with the real id. One validator, one set of rules, no second
     * copy: `validateRequestInput` has no field-subset mode, and inventing one
     * here would be exactly the drift this codebase's own comments forbid.
     *
     * ⭐ IT ALSO FIXES THE SECOND FINDING, and that is not a coincidence — they
     * were one defect. `createTypedResident`'s own room precheck used to
     * short-circuit the submit, so a refused attempt rendered ONLY "Enter their
     * room." while the equally empty next-of-kin fields showed no error and no
     * `aria-invalid`. With validation first, every invalid field gets its message
     * and its state, and `focusFirstError` lands on the first in SCREEN order.
     */
    const IDENTITY_PROBE = '1';
    // ⛔⛔ Board 2638 (plan D3) — A NEW RESIDENT RE-READS THE SERVER'S CLOCK FIRST.
    // The estimate can lag by however long a ward iPad slept (WebKit stops
    // `performance.now()` in sleep), and a lagging "now" would let a past or
    // too-soon appointment through this probe and write the PERMANENT Article 9
    // row above before the server refused the request. So the anchor is
    // refreshed from `/api/meta`'s `serverNow` immediately before the probe;
    // `meta` itself is not touched, and with a fresh anchor the probe applies
    // ALL FIVE bounds (see `serverClock`). An existing resident needs no
    // refresh and stays lenient, because a lenient probe there costs one server
    // 400 with nothing written.
    // ⛔ A FAILED RE-ANCHOR STOPS THE SEND, nothing written (security review L2):
    // keeping a stale or epoch anchor here would fail open onto the permanent
    // row. The messages are `loadMeta`'s: the network one for no response, the
    // generic one for anything else (a non-200, or no readable `serverNow`).
    // ⛔ AND THE HOME'S OFFSET FROM THAT SAME REFRESH (security review round 2):
    // the server derives the collection from the home's CURRENT offset, so a
    // probe using the page-load one could pass a collection the server calls
    // past if an operator raised it meanwhile. `?? null`, never 60, as at load.
    // `meta` and the Collection preview are untouched (they keep the load value).
    let probeOffsetMinutes = pickupOffsetMinutes;
    // ⭐ Board 2706 rework (R1, round 2): who pressed Send, for the one await before the send
    // begins (the re-read below is a read, so nothing is held for it yet).
    const pressedBy = accountRef.current;
    if (pendingCreation) {
      // Busy across the await, as the resident create below is, so a second
      // tap cannot start a second send while this one is still reading.
      setSubmitting(true);
      const fresh = await api.get<MetaResponse>('/api/meta');
      setSubmitting(false);
      // ⛔ GONE WHILE READING: nothing is written and nothing was begun, so the plain draft the
      // shell already holds is what she gets back.
      if (!mountedRef.current || accountRef.current !== pressedBy) return;
      if (fresh.kind === 'network-error') {
        setSubmitError(sharedCopy.networkError);
        return;
      }
      if (fresh.status !== 200 || parseServerNow(fresh.body?.serverNow) === null) {
        setSubmitError(sharedCopy.genericError);
        return;
      }
      recordServerClock(fresh.body.serverNow);
      probeOffsetMinutes = fresh.body.home?.pickupOffsetMinutes ?? null;
    }
    const probe = validateRequestInput(
      assembleRequestPayload({
        ...effectiveValues,
        ...(isNewResident ? { residentId: IDENTITY_PROBE } : {}),
        // board 2732 M5: a staged requester stands in for its id, exactly as a new resident does.
        ...(isNewRequester ? { requestedByStaffId: IDENTITY_PROBE } : {}),
      }),
      // Every bound for a pending creation (the anchor is fresh); lower only otherwise.
      { now: estimatedServerNow(), lowerBoundsOnly: !pendingCreation, pickupOffsetMinutes: probeOffsetMinutes },
    );
    // ⛔ THE TICK IS REFUSED HERE, WITH THE PROBE, AND THAT POSITION IS THE WHOLE POINT. The probe
    // guarantees nothing is written until the form is valid; the tick is not in the payload so the
    // probe cannot see it, and refusing it later lets `createTypedResident` below write a
    // PERMANENT Article 9 roster row for a journey that never happens.
    const probeConfirmErrors = payerConfirmErrors(effectiveValues);
    if (!probe.ok || Object.keys(probeConfirmErrors).length > 0) {
      const merged = { ...(probe.ok ? {} : probe.errors), ...probeConfirmErrors };
      setErrors(merged);
      recordWholesaleResidentRefusal(merged);
      focusFirstError(merged);
      return;
    }

    // ⭐ Board 2706 rework (R1, round 2): the send begins HERE, before its first write (the
    // resident create below, or the request itself).
    const run = beginSend(draftNow(effectiveValues, residentQuery));

    // ⭐ board 2732 M5 (DD4): THE STAFF WRITE FIRST, before the Article 9 resident
    // write, so a refused staff add (a duplicate, a deactivated name, the ceiling,
    // the throttle) stops the send with nothing else written. The created id is
    // carried into the held draft, so a lock restore never adds the person twice.
    let requesterPick: Pick<RequestFormValues, 'requestedByStaffId' | 'requesterQuery' | 'requesterAdd'> | null = null;
    if (isNewRequester) {
      setSubmitting(true);
      const staff = await createTypedRequester(effectiveValues);
      setSubmitting(false);
      if (!staff) {
        failSend(run);
        return;
      }
      const pick = { requestedByStaffId: String(staff.staffId), requesterQuery: staff.displayName, requesterAdd: false };
      requesterPick = pick;
      effectiveValues = { ...effectiveValues, ...pick };
      setValues((prev) => ({ ...prev, ...pick }));
      moveSend(run, draftNow(effectiveValues, residentQuery));
      // ⛔ No further write for somebody who is gone (see `SendRun`).
      if (!stillMine(run)) {
        failSend(run);
        return;
      }
    }

    // A typed-but-unknown name is a NEW resident: every OTHER field is now known
    // good, so creating them is the last thing that can be premature.
    let createdName: string | null = null;
    if (isNewResident) {
      setSubmitting(true);
      const created = await createTypedResident(effectiveValues);
      setSubmitting(false);
      if (!created) {
        failSend(run);
        return;
      }
      createdName = created.displayName;
      // ⛔ THE FIELD TAKES THE SERVER'S SPELLING. The store cleaned the name on
      // the way in, so this is what the roster now holds; leaving the raw typed
      // text in the box makes every later re-match depend on the client and the
      // server normalising identically, which is the coupling this defect was.
      setResidentQuery(created.displayName);
      // ⛔ R2 — THROUGH THE EPOCH, never a bare `setValues`. The new resident is
      // an ARRIVAL like any other; `keepValues` says the three fields were
      // authored under this very person, because she is the one who typed them.
      effectiveValues = applyResidentMatch(
        { id: created.id, displayName: created.displayName, room: effectiveValues.room },
        true,
      );
      // ⛔ board 2732 M5: `applyResidentMatch` builds from the RENDER's values, which
      // predate the staff write above, so the picked requester is laid back on
      // (in the returned copy and in state) or the request would leave without it.
      if (requesterPick !== null) {
        const pick = requesterPick;
        effectiveValues = { ...effectiveValues, ...pick };
        setValues((prev) => ({ ...prev, ...pick }));
      }
      // The resident now exists: the held copy names her, so a send that stops before the
      // request hands back a draft that will not create her a second time.
      moveSend(run, draftNow(effectiveValues, created.displayName));
    }

    // ⭐ Board 2647 (P2): WITH THE PROBE'S OFFSET. A lowered offset passed the
    // probe, wrote the resident, then failed the page-load offset's
    // "collection not already past" bound here and never posted.
    await sendRequest(effectiveValues, createdName, probeOffsetMinutes, run);
  }

  function startAnother(): void {
    // ⛔ R2 — THE IDENTITY EPOCH IS ENDED THROUGH THE ONE FUNCTION, then the rest
    // of the form is blanked. Setting `values` here directly is what let earlier
    // batches leave a piece behind; `applyResidentMatch(undefined)` clears the
    // residentId, the authorship, the consent, the notice and the offer in one
    // act, and the empty form below overwrites the three field values anyway.
    applyResidentMatch(undefined);
    setValues(emptyRequestFormValues());
    setResidentQuery('');
    setErrors({});
    // The whole error map goes, so the provenance of the one message that had
    // any must go with it - `writeFields` is not the door here (see its note).
    residentRefusedAbout.current = null;
    setSubmitError(null);
    setSuccess(null);
  }

  if (metaError) {
    return (
      <div class="screen new-request-screen">
        <p class="field-error" role="alert">
          {metaError}
        </p>
        <button type="button" class="btn btn--primary" onClick={() => void loadMeta()}>
          {sharedCopy.tryAgain}
        </button>
      </div>
    );
  }

  if (!meta) {
    return (
      <p class="loading-notice" role="status">
        {sharedCopy.loading}
      </p>
    );
  }

  if (success) {
    return (
      <div class="screen new-request-screen">
        {/* ⛔ T1101 — ON A TOTAL ALERT FAILURE THE HEADING AND BODY ARE REPLACED,
            not merely annotated. The success copy claims the request "is on its
            way to Helen", and leaving that claim on screen under a notice that
            contradicts it is worse than saying nothing. `role="alert"` because
            this appears after the panel has already rendered once for a
            screen-reader user in the ordinary case. */}
        {/* ⛔ `tabIndex={-1}` IS NOT DECORATION - it is what makes the heading a
            legal focus target for the effect above. Remove it and `.focus()`
            silently does nothing, which is exactly the shape of the defect. */}
        <h1 ref={successTitleRef} tabIndex={-1}>
          {success.alertFailed ? requestFormCopy.alertFailedTitle : requestFormCopy.successTitle}
        </h1>
        {success.alertFailed && (
          <p class="notice" role="alert">
            {requestFormCopy.alertFailedNotice(success.jobRef)}
          </p>
        )}
        {/* ⭐ T1683 M3 (A1) - THE REFERENCE ON THE SUCCESS PATH TOO, not only on
            the failure one. The nurse is told her request is on its way to
            Helen; A1's whole point is that she can ring about it by name, and a
            reference she only ever sees when something went wrong is a
            reference she has never seen.
            ⛔ OMITTED ENTIRELY WHEN NULL. Every request written before M3 has no
            reference and never will; a blank label would read as one that failed
            to load. */}
        {success.jobRef !== null && (
          <p class="job-ref">{requestFormCopy.jobRefLabel(success.jobRef)}</p>
        )}
        {/* ⛔⛔ THE ROSTER REFUSAL SURVIVES ONTO THIS PANEL, AND WITHOUT THIS
            LINE THE TICK IS SILENT. P1-2 says the refusal shows beside the
            checkbox and the request carries on — but a 201 REPLACES the whole
            form, so on the common path (roster refused, request accepted) the
            notice would be unmounted in the same render that reported success.
            The nurse would have ticked "Also update the resident list", been
            told her request was sent, and never learned the list was not
            updated. It renders here as well, on the panel that is actually on
            screen. */}
        {rosterUpdateNotice !== null && (
          <p class="notice" role="alert">
            {rosterUpdateNotice}
          </p>
        )}
        <p>
          {success.alertFailed
            ? requestFormCopy.alertFailedBody(success.residentName, success.destinationName)
            : requestFormCopy.successBody(success.residentName, success.destinationName)}
        </p>
        <button type="button" class="btn btn--primary" onClick={onLock}>
          {requestFormCopy.doneLockButton}
        </button>
        <button type="button" class="btn btn--secondary" onClick={startAnother}>
          {requestFormCopy.anotherRequestButton}
        </button>
      </div>
    );
  }

  /**
   * ⛔ THE CLASS IS RESOLVED HERE, NOT INSIDE `class={…}`, AND THAT IS A GATE
   * CONSTRAINT RATHER THAN A STYLE CHOICE. `style-contract.spec.ts` reads the
   * whole `class={…}` region and treats every string literal in it as a class
   * being applied — so `class={prefilledNow().includes('room') ? … }` made it
   * report the field KEYS as undefined classes (measured when there were three
   * of them: `room`, `nextOfKinName`, `nextOfKinPhone`, and the allow-list went
   * from empty to three). ⚠️ TWO OF THOSE THREE ARE GONE AT T1868 M6 and the
   * constraint is NOT weaker for it — one key in that region is one undefined
   * class. Keeping the field KEYS out of it is what keeps the gate's allow-list
   * empty without weakening it. The class itself is graded at RUNTIME, in
   * `request-form-prefill.spec.tsx`, which is the stronger check anyway.
   */
  const filledNow = prefilledNow();
  const mark = (k: (typeof PREFILLED_FIELDS)[number]): string | undefined =>
    filledNow.includes(k) ? 'field--prefilled' : undefined;
  const prefilledClass = {
    room: mark(PREFILLED_FIELDS[0]),
  };

  /**
   * ⭐⭐ ONE RENDERER FOR EVERY FIELD ERROR ON THIS FORM — T1717 M7, ux-review
   * WARN on the live drive.
   *
   * ⛔ WHAT THE GAP WAS. M5 wired `aria-describedby` for the two RETURN inputs
   * and nothing else. Every other field on this form — resident, room,
   * destination, the free-text destination, payer, the outbound date and time,
   * notes (and, until T1868 M6, the next-of-kin name and phone) — rendered
   * `aria-invalid="true"` and a
   * visible `.field-error` with **no association between them**. To a screen
   * reader that is a control announced as "invalid" with no reason given, and a
   * paragraph floating somewhere in the form that may never be read at all.
   * `aria-invalid` says THAT something is wrong; only `aria-describedby` says
   * WHAT.
   *
   * ⛔ WHY A HELPER RATHER THAN THIRTEEN MORE HAND-WIRED PAIRS. The association
   * is a THREE-PART invariant — the control's `aria-describedby`, the
   * paragraph's `id`, and the two being the same string — and a hand-written
   * pair fails SILENTLY in every direction: a typo'd id points at nothing, a
   * missing id makes the reference dangle, and a copy-pasted id from the field
   * above points at somebody else's message. `errorId` is the single source of
   * that string, so the three parts cannot drift.
   *
   * ⚠️ `role="alert"` ON EVERY ONE, matching M5's return pair rather than the
   * older bare paragraphs: after a refused submit the messages are inserted, and
   * an inserted paragraph with no role is announced by nothing. This is a
   * WIDENING of M5's treatment to the fields that never got it, not a new idiom.
   *
   * ⚠️ NOT APPLIED TO: the resident-removed OFFER (it is an offer with its own
   * button, not a field error), the alert-failure panel, and the two checkbox
   * ticks — a checkbox is always in a stateable state, so `aria-invalid` on one
   * would assert something false (see their own comments below).
   */
  const errorId = (field: string): string => `rf-${field}-error`;

  /** The a11y props a control carries WHILE its field is in error. Spread onto
   *  the control: `{...errorProps('room')}`. Absent when there is no error, so
   *  no control ever points at a paragraph that is not rendered. */
  const errorProps = (
    field: string,
  ): { 'aria-invalid': 'true'; 'aria-describedby': string } | Record<string, never> =>
    errors[field] === undefined ? {} : { 'aria-invalid': 'true', 'aria-describedby': errorId(field) };

  /**
   * ⛔ T1868 M2 (wave-2 gate BLOCK) — THE A11Y PROPS FOR A CONTROL TWO KEYS CAN
   * ACCUSE. `#rf-date` is the control for BOTH `journeyDate` (the date is present
   * but wrong) and `journey` (no date was given at all), and `errorProps` keys on
   * one field, so whichever key it did not name left the control unmarked. Adding
   * the `ERROR_FIELD_IDS` entry moved FOCUS there but not `aria-invalid` or
   * `aria-describedby` — measured: focus landed correctly and the control was
   * still announced as valid, which is worse than not moving at all, because a
   * screen reader then says nothing is wrong with the field it just landed on.
   * ⇒ Takes the fields in priority order and uses the FIRST one actually in error.
   */
  const errorPropsFirst = (
    ...fields: string[]
  ): { 'aria-invalid': 'true'; 'aria-describedby': string } | Record<string, never> => {
    const hit = fields.find((f) => errors[f] !== undefined);
    return hit === undefined ? {} : { 'aria-invalid': 'true', 'aria-describedby': errorId(hit) };
  };

  /**
   * ⭐⭐ T2088 M4 (ux-review WARN) — A CONTROL WHOSE STANDING HINT IS THE WHOLE
   * AFFORDANCE MUST REFERENCE IT ALWAYS, AND ITS ERROR FIRST WHEN THERE IS ONE.
   *
   * ⛔ WHAT THE GAP WAS. `errorPropsFirst` emits `aria-describedby` ONLY while a
   * field is in error, and returns `{}` otherwise — correct for every control
   * whose description IS its message. The return time is not one of those: D3
   * rejected a second checkbox precisely because the hint sentence carries the
   * meaning of a blank ("Leave this blank if you do not know yet"), so a screen
   * reader user who tabbed to `Coming back at` in its ordinary, error-free state
   * heard the label and nothing else. Measured live: `aria-describedby` was
   * `null`. WCAG 3.3.2 / 1.3.1, Level A.
   *
   * ⛔ THE THREE-PART INVARIANT ABOVE IS UNTOUCHED, and that is why this is a
   * SEPARATE helper rather than a change to `errorPropsFirst`: every other
   * control on this form keeps the exact props it had, `errorId` stays the one
   * source of the error string, and the ERROR id is emitted FIRST in the list so
   * it is announced before the standing hint (WAI-ARIA reads an
   * `aria-describedby` id list in order). The hint id is a constant, so the
   * reference and the paragraph's own `id` cannot drift either.
   * ⚠️ APPLIED TO THIS ONE CONTROL ONLY. The other `.field-hint` paragraphs on
   * this form are unreferenced too — some deliberately (`aria-hidden` where a
   * live region carries the same sentence, `role="status"` where it is an
   * announcement) — and the form-wide sweep is a follow-up, not this milestone.
   */
  const errorPropsFirstDescribedBy = (
    hintId: string,
    ...fields: string[]
  ): { 'aria-invalid'?: 'true'; 'aria-describedby': string } => {
    const hit = fields.find((f) => errors[f] !== undefined);
    return hit === undefined
      ? { 'aria-describedby': hintId }
      : { 'aria-invalid': 'true', 'aria-describedby': `${errorId(hit)} ${hintId}` };
  };

  /** The message paragraph for `field`, or nothing. Its `id` is the SAME string
   *  `errorProps` points at, by construction. */
  const fieldError = (field: string): JSX.Element | null => {
    const message = errors[field];
    if (message === undefined) return null;
    return (
      <p class="field-error" role="alert" id={errorId(field)}>
        {message}
      </p>
    );
  };

  /**
   * ⭐⭐ T2075 M3 (plan D12, R-3) — WHO IS REQUESTING, FIRST AND ALWAYS.
   *
   * The list is the server's (`meta.staff`, scoped on the session's role and
   * group, recently used first). ⭐ board 2732 M5: a `group` account types into
   * one `FormField kind="input"` and picks from suggestions (see
   * `onRequesterInput`); `reception` and `admin` choose a group over
   * `meta.groups` first and the box is narrowed to it (disabled until a group is
   * chosen). With no names to offer, a ward account that can add keeps the box;
   * everyone else reads a role-specific `EmptyState`, and the send keeps
   * refusing on the validator's required key.
   * ⛔ `FormField` OWNS THE ERROR WIRING for this control (`aria-invalid`,
   * `aria-describedby`, the message under the field), which is the same
   * three-part invariant `errorProps` / `fieldError` hold for the hand-built
   * controls below; the message is the validator's own, as everywhere else.
   * ⛔ THE CENSUS COUNTS PER ELEMENT: one `FormField` contributes
   * `'field-label': 1, 'login-input': 1` to this screen's class-level debt by
   * construction (`test/client/lib/adoption-debt.ts`), which is why the two
   * RequestForm pins there re-key in the same change and the reception shape is
   * registered as its own screen.
   */
  const staff = meta.staff ?? [];
  const groups = meta.groups ?? [];
  const groupPicksFirst = role !== 'group';
  const staffAddable = meta.staffAddable === true;
  // ⭐ board 2732 M5 (R-8) — WHAT AN EMPTY ROSTER SAYS, per role: a ward account
  // that can add keeps the box (and the in-place `requesterEmpty` tells it to
  // type its name and tap Add); otherwise a neutral `EmptyState` points at whoever
  // can fill the list. Never "ask your administrator" to an admin.
  const requesterEmptyState =
    staff.length > 0 || (role === 'group' && staffAddable)
      ? null
      : role === 'admin'
        ? requesterBoxCopy.emptyAdmin
        : role === 'reception'
          ? requesterBoxCopy.emptyReception
          : requesterBoxCopy.emptyWardNoAdd;
  const requesterHintGroup = role === 'group' ? groupLabel : values.requesterGroup;
  // Once per render: the chips, the live region and the visible count read the same matches.
  const requesterFound = requesterMatches();
  const requesterCount = requesterMatchCount(requesterFound);
  const requesterAddShown = requesterAddOffered();
  const requesterAwaitsGroup = groupPicksFirst && values.requesterGroup === '';
  const requesterHint = requesterAwaitsGroup
    ? requesterBoxCopy.chooseGroupFirst
    : staff.length === 0
      ? requestFormCopy.requesterEmpty
      : requestFormCopy.requesterRecentHint;
  // board 2186: the destination's class drives the payer set, the double-check and the free-text
  // box. Derived once here so the three cannot disagree within a render.
  const isOtherDestination = isOtherDestinationOf(values);

  return (
    <form class="screen new-request-screen" onSubmit={(e) => void submit(e)}>
      {/* ⭐ Board 2706 rework (R5): a legal focus target, so a sign-in lands here (`app.tsx`)
          rather than on the page body, as the success heading already is. */}
      <h1 tabIndex={-1}>{requestFormCopy.title}</h1>

      {requesterEmptyState !== null ? (
        <>
          <EmptyState headline={requesterEmptyState} />
          {fieldError('requestedByStaffId')}
        </>
      ) : (
        <>
          {groupPicksFirst && (
            <FormField
              id="rf-requester-group"
              kind="select"
              label={requestFormCopy.requesterGroupLabel}
              value={values.requesterGroup}
              options={[
                { value: '', label: requestFormCopy.requesterGroupPlaceholder },
                ...groups.map((g) => ({ value: g, label: g })),
              ]}
              onInput={(g) =>
                // A new group empties the pick, the text and a staged add: the name may not be in it.
                writeFields(['requestedByStaffId'], (prev) => ({
                  ...prev,
                  requesterGroup: g,
                  requestedByStaffId: '',
                  requesterQuery: '',
                  requesterAdd: false,
                }))
              }
            />
          )}
          {/* ⭐⭐ board 2732 M5 (DD3) — A TEXT BOX, THE RESIDENT BOX'S PATTERN. The
              label, the error wiring and the focus target (`rf-requester`, row 0 of
              `ERROR_FIELD_IDS`) are unchanged from the select it replaces. */}
          <FormField
            id="rf-requester"
            kind="input"
            label={requestFormCopy.requesterLabel}
            value={values.requesterQuery}
            placeholder={requestFormCopy.requesterPlaceholder}
            hint={requesterHint}
            error={errors['requestedByStaffId']}
            disabled={requesterAwaitsGroup}
            autoComplete="off"
            spellcheck={false}
            maxLength={120}
            onInput={onRequesterInput}
          />
          {/* MOUNTED FROM THE FIRST RENDER, the resident region's rule: a live
              region announces changes to its own text. A count, never a name. */}
          <p class="visually-hidden" role="status" aria-live="polite" id="rf-requester-matches">
            {requesterCount}
          </p>
          {(requesterFound.shown.length > 0 || requesterAddShown) && (
            <>
              <div class="resident-suggestions">
                {requesterFound.shown.map((s) => (
                  <button
                    key={s.staffId}
                    type="button"
                    class="btn btn--quiet resident-suggestion"
                    onClick={() => pickRequesterSuggestion(s)}
                  >
                    {s.displayName}
                  </button>
                ))}
                {/* The Add chip comes AFTER the matches: the existing names are
                    the answer that avoids a duplicate row. */}
                {requesterAddShown && (
                  <button type="button" class="btn btn--quiet resident-suggestion" onClick={stageRequesterAdd}>
                    {requesterBoxCopy.addChip(values.requesterQuery.trim())}
                  </button>
                )}
              </div>
              {requesterFound.shown.length > 0 && (
                <p class="field-hint" aria-hidden="true">
                  {requesterCount}
                </p>
              )}
            </>
          )}
          {values.requesterAdd && values.requestedByStaffId === '' && (
            <p class="field-hint">
              {requesterHintGroup !== null && requesterHintGroup !== ''
                ? requesterBoxCopy.addHint(values.requesterQuery.trim(), requesterHintGroup)
                : requesterBoxCopy.addHintNoGroup(values.requesterQuery.trim())}
            </p>
          )}
        </>
      )}

      <label class="field-label" for="rf-resident">
        {requestFormCopy.residentLabel}
      </label>
      <input
        id="rf-resident"
            {...errorProps('residentId')}
        type="text"
        maxLength={120}
        placeholder={requestFormCopy.residentPlaceholder}
        value={residentQuery}
        onInput={(e) => onResidentInput((e.target as HTMLInputElement).value)}
        onBlur={onResidentBlur}
      />
      {/* ⭐⭐ A2-newrequest-1 (T1723 M4d) - MOUNTED FROM THE FIRST RENDER, AND
          THAT IS THE WHOLE MECHANISM. Until now this region lived INSIDE the
          conditional block below, so it was inserted into the document already
          holding its text: a live region announces CHANGES to its own contents,
          and an assistive technology that first meets a node with the sentence
          already in it has nothing to announce. The security review measured it
          as "mounts with its content". So the node is always here and only its
          TEXT moves, which is the change an `aria-live` region exists to report.
          ⛔ `.visually-hidden`, NEVER `.field-hint`, and that is a LAYOUT rule
          rather than a styling preference: `form` sets `gap` between its
          children, so an always-present in-flow `<p>` holding `''` would paint a
          12px hole under the Resident box on every screen that never types a
          name. `.visually-hidden` (app.css:1442) is position:absolute and out of
          flow, so an empty one occupies nothing. That is the A2-myresidents-3
          class of defect, avoided rather than fixed later.
          ⛔ AND THE VISIBLE HINT BELOW IS `aria-hidden`, because its text is now
          carried here: two nodes with the same sentence is the same sentence
          read twice.
          ⚠️ THE STRING IS INLINE, NOT IN `client/copy.ts`, because that file is
          fenced for this milestone; folding it in is reported as the residual. */}
      <p class="visually-hidden" role="status" aria-live="polite" id="rf-resident-matches">
        {residentMatchCount()}
      </p>
      {(() => {
        const { shown: suggestions } = residentMatches();
        if (residentQuery.trim().length === 0 || values.residentId !== '') return null;
        return (
          <>
            {suggestions.length > 0 && (
              <div class="resident-suggestions">
                {suggestions.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    class="btn btn--quiet resident-suggestion"
                    onClick={() => pickSuggestion(r)}
                  >
                    {residentPickerLabel(r)}
                  </button>
                ))}
              </div>
            )}
            {/* ⛔ TOLD BEFORE SHE TYPES MORE, not after the 403. In real mode
                `POST /api/residents` refuses, and the promise "will be added to
                your home's list" is simply false there. Saying so up front also
                REDUCES Article 9 exposure: today she types a resident's name
                and the form POSTs it before the refusal.
                ⚠️ Residual, stated rather than implied: this copy does not stop
                that POST. Closing it is a behaviour change on the parked gate. */}
            {/* ⛔⛔ SUPPRESSED WHILE THE RESIDENT FIELD CARRIES AN ERROR — T1717
                M4, closing the duplication M3 measured and handed forward. In
                the closed posture `newResidentHintReal` and
                `addResidentReadOnly` BOTH end with "Adding residents is not
                switched on here yet", so after a refused submit that sentence
                rendered TWICE, one line apart, in a hint and an error. Both
                strings are prescribed verbatim by P1-1 so neither may be
                re-worded; what changes is which one is on screen. The ERROR
                wins, because it is the one that says what to do next and the one
                that reports something that has actually happened.
                ⚠️ The SUGGESTIONS above are deliberately NOT suppressed: they
                are the way out of the error, not a second copy of it. */}
            {/* ⭐⭐ A2-newrequest-1 (T1723 M4) - THE MATCHES ANNOUNCE THEMSELVES.
                Four buttons appearing under the field is a purely VISUAL event:
                a screen-reader user typing here was given a choice she was never
                told existed. This is the file's own mechanism from 80 lines
                below (`role="status" aria-live="polite"`, the prefilled
                notice), reused rather than reinvented.
                ⛔ NOT A COMBOBOX, and that is a decision rather than a shortcut.
                `role="combobox"` + `aria-activedescendant` is a keyboard
                contract -- arrow keys, Escape, a managed active option -- and
                half of one is worse than none: it would promise navigation the
                buttons do not implement. The count plus "tap one" describes what
                is actually on the screen.
                ⛔⛔ THE ANNOUNCING HALF MOVED OUT (M4d): this paragraph is the
                SIGHTED copy only. It carries no `role` and no `aria-live` any
                more, and it is `aria-hidden` - `#rf-resident-matches` above is
                the one node an assistive technology reads, and it says the same
                sentence from the same function.
                ⭐ WHICH NOW STATES THE CAP. Six is the list's Helen-test budget
                and it stays, but a seventh match used to be silently dropped
                under the words `6 matching names`, which is a count that is
                simply false about the roster. */}
            {suggestions.length > 0 && (
              <p class="field-hint" aria-hidden="true">
                {residentMatchCount()}
              </p>
            )}
            {/* ⛔⛔ II-portal-#18 — ONE MESSAGE AT A TIME. Until now the screen
                offered a CHOICE ("Aoife", "Aoife Byrne") and announced a
                CREATION ("New resident: Aoife will be added to your home's
                list") in the same instant, which are contradictory answers to
                the same keystroke: one says "she is already here, pick her", the
                other says "she is not, I will make her". The matches win while
                they are on screen -- they are the answer that avoids a duplicate
                resident row -- and the creation line returns the moment the
                typed name matches nobody, which is when it is true. */}
            {suggestions.length === 0 && !errors['residentId'] && (
              <p class="field-hint">
                {residentsWritable
                  ? requestFormCopy.newResidentHint(residentQuery.trim())
                  : requestFormCopy.newResidentHintReal(residentQuery.trim())}
              </p>
            )}
          </>
        );
      })()}
      {fieldError('residentId')}
      {/* ⭐ DD10 — THE OFFER, NOT A REFUSAL. The server answered 409
          `resident_removed` with the row's own id: this person is on the home's
          list, just retired from it. One tap puts them back and carries the same
          send through, so one person keeps one row and one `resident_id` for the
          erasure sequence to reach. ⛔ The wording is `myResidentsCopy`'s, shared
          with the roster tab, because it is the same offer in the same words. */}
      {removedOffer && (
        <div class="resident-removed-offer">
          <p class="field-error" role="alert">
            {myResidentsCopy.removedOffer(removedOffer.displayName)}
          </p>
          <button
            type="button"
            class="btn btn--secondary"
            disabled={submitting || sendPending}
            onClick={() => void putResidentBack()}
          >
            {myResidentsCopy.putBackButton}
          </button>
        </div>
      )}

      {/* ⭐⭐ ux-review (High) — THE PREFILL ANNOUNCES ITSELF. Controls used to
          fill with no visible change and no announcement: a sighted nurse can
          miss it, and a screen-reader user got nothing at all.
          ⛔ `role="status"` + `aria-live="polite"`, so it is spoken WITHOUT
          interrupting her typing — `role="alert"` here would talk over the field
          she is in, on a screen whose whole constraint is calm.
          ⛔ IT STILL NAMES WHAT WAS ACTUALLY FILLED rather than announcing a
          fixed sentence, and it disappears the moment the match clears, because
          `prefilledNow()` is derived from the same snapshot that decides
          ownership — there is no second flag to forget to reset.
          ⚠️ ONE FIELD SINCE T1868 M6, so the three-way branch is gone. It is
          still driven from `prefilledNow()` and not from `residentId`: a
          resident whose room the nurse has already edited is matched but has
          nothing prefilled, and announcing a fill then would be false. */}
      {prefilledNow().length > 0 && (
        <p class="field-hint" role="status" aria-live="polite">
          {requestFormCopy.prefilledNotice()}
        </p>
      )}

      <label class="field-label" for="rf-room">
        {requestFormCopy.roomLabel}
      </label>
      <input
        id="rf-room"
        class={prefilledClass.room}
            {...errorProps('room')}
        type="text"
        maxLength={20}
        value={values.room}
        onInput={(e) => editPrefilledField('room', (e.target as HTMLInputElement).value)}
      />
      {/* ⚠️ SUPPRESSED, NOT RE-WORDED, when residents are read-only. "It is
          saved to your resident list too" has no true real-mode variant, and
          inventing one would state a promise the server refuses. This is the
          one place the paired "the real variant IS rendered" rule in the specs
          deliberately does not apply. */}
      {residentsWritable && residentQuery.trim().length > 0 && values.residentId === '' && (
        <p class="field-hint">{requestFormCopy.roomNewResidentHint}</p>
      )}
      {fieldError('room')}

      {/* ⚠️ THE GROUP CARRIES THE ASSOCIATION, NOT A CONTROL. There is no single
          control to mark `aria-invalid` here — the answer is the SET of ticks —
          so the association goes on the `<fieldset>`, which is what a screen
          reader announces on entering the group (T1717 M7). */}
      <fieldset {...errorProps('equipment')}>
        <legend>{requestFormCopy.equipmentLegend}</legend>
        {/* ⛔ THE FORM LIST, NEVER `EQUIPMENT_OPTIONS` (T2174). That tuple is the
            WRITE ALLOW-LIST and still carries `oxygen` so a stored row re-POSTs;
            this one is what the nurse is offered. Rendering the allow-list here
            is the revert that puts Oxygen back in front of her.
            ⛔ The description is a SIBLING of the label, never inside it: the
            label wraps the input, so text placed within would join the
            accessible name and break every `getByLabelText`. That sibling is
            also why `.field-hint + .checkbox-label` exists in `app.css` — it
            restores the row separation `.checkbox-label + .checkbox-label` can
            no longer provide once a `<p>` sits between the rows. */}
        {EQUIPMENT_FORM_OPTIONS.map((opt) => (
          <Fragment key={opt}>
            <label class="checkbox-label" for={`rf-eq-${slugForId(opt)}`}>
              <input
                id={`rf-eq-${slugForId(opt)}`}
                type="checkbox"
                checked={values.equipment.includes(opt)}
                aria-describedby={`rf-eq-${slugForId(opt)}-desc`}
                onChange={(e) => toggleEquipment(opt, (e.target as HTMLInputElement).checked)}
              />
              {requestFormCopy.equipmentLabels[opt] ?? opt}
            </label>
            <p class="field-hint" id={`rf-eq-${slugForId(opt)}-desc`}>
              {requestFormCopy.equipmentDescriptions[opt]}
            </p>
          </Fragment>
        ))}
      </fieldset>
      {fieldError('equipment')}

      <label class="field-label" for="rf-destination">
        {requestFormCopy.destinationLabel}
      </label>
      <select
        id="rf-destination"
            {...errorProps('destinationId')}
        value={values.destinationId}
        onChange={(e) => setDestination((e.target as HTMLSelectElement).value)}
      >
        <option value="">{requestFormCopy.destinationPlaceholder}</option>
        {meta.destinations.map((d) => (
          <option key={d.id} value={String(d.id)}>
            {d.name}
          </option>
        ))}
        <option value="other">{requestFormCopy.destinationOtherOption}</option>
      </select>
      {/* ⛔ T1683 M4 - AN EMPTY CURATED LIST IS EXPLAINED, NEVER LEFT BLANK. Until
          a home's account is linked and its priced destinations projected, this
          select holds only the placeholder and the free-text option; without
          this line that reads as a screen that failed to load. */}
      <p class="field-hint">
        {meta.destinations.length === 0
          ? requestFormCopy.destinationEmptyHint
          : requestFormCopy.destinationHint}
      </p>
      {fieldError('destinationId')}
      {isOtherDestination && (
        <>
          <label class="field-label" for="rf-destination-text">
            {requestFormCopy.destinationTextLabel}
          </label>
          {/* ⛔ THE BOUND IS DERIVED, NEVER HAND-KEYED (board 2190). What is
              STORED is the canonical reconstruction, which can be up to
              DESTINATION_TEXT_GROWTH characters LONGER than what was typed, so
              a browser cap of the full DESTINATION_TEXT_MAX_LEN would let a
              nurse fill the box and then be refused by the server on a value
              she cannot see or shorten. ⚠️ The other `maxLength` on this screen
              belongs to the RESIDENT SEARCH box; do not sweep them together. */}
          <input
            id="rf-destination-text"
            {...errorProps('destinationText')}
            type="text"
            inputMode="text"
            autocapitalize="characters"
            autocomplete="off"
            spellcheck={false}
            maxLength={DESTINATION_TEXT_MAX_LEN - DESTINATION_TEXT_GROWTH}
            value={values.destinationText}
            onInput={(e) => setField('destinationText', (e.target as HTMLInputElement).value)}
          />
          {/* ⛔ THE PORTAL'S FIRST RENDERED ANCHOR (Alex, 2026-09-18, board 2190).
              `rel` carries BOTH tokens deliberately: `noopener` is implied by
              `target="_blank"` in every current browser, but `noreferrer` is NOT
              — and it is the one that matters here, because the portal sets no
              CSP, so without it the Referer hands a portal URL to a third party.
              Spelling `noopener` too costs nothing and survives a future reader
              who drops the target. */}
          <p class="field-hint">
            {requestFormCopy.destinationTextHint}{' '}
            <a
              class="field-hint-link"
              href={requestFormCopy.destinationFinderUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {requestFormCopy.destinationFinderLabel}
              <span class="visually-hidden"> {requestFormCopy.destinationFinderNewTab}</span>
            </a>
          </p>
          {fieldError('destinationText')}
        </>
      )}

      {/* Same group treatment as the equipment fieldset above. */}
      <fieldset {...errorProps('payerKind')}>
        <legend>{requestFormCopy.payerLegend}</legend>
        {/*
          ⛔ THE OFFERED SET IS THE DESTINATION'S, NOT A CONSTANT (board 2186). A price-book
          destination is billed to the home account, so Cash / card is not on offer there; the
          free-typed branch offers all three. The SAME `allowedPayerKinds` the server enforces is
          used here, so the form can never offer a value the server would refuse.
          ⚠️ These render from `PAYER_KINDS`, NOT from `meta.payerKinds` (declared but unread) —
          `/api/meta` still advertises all three, deliberately: that list is account-level, while
          this narrowing is per-destination.
        */}
        {allowedPayerKinds(isOtherDestination).map((kind) => (
          <label key={kind} class="radio-label" for={`rf-payer-${kind}`}>
            <input
              id={`rf-payer-${kind}`}
              type="radio"
              name="payerKind"
              value={kind}
              // ⭐ Board 2706 rework (R5): the radio focus lands on carries the state and the
              // reason, as every other refused control does; the fieldset alone was not announced.
              {...errorProps('payerKind')}
              checked={values.payerKind === kind}
              // ⛔ Board 2706: the tick goes in the SAME update as the payer, so a tick given
              // under Cash / card never survives a switch away and back (as `setDestination`).
              onChange={() =>
                writeFields(['payerKind', 'payerConfirmed'], (prev) => ({
                  ...prev,
                  payerKind: kind,
                  payerConfirmed: false,
                }))
              }
            />
            {requestFormCopy.payerLabels[kind]}
          </label>
        ))}
      </fieldset>
      {fieldError('payerKind')}
      {/*
        ⛔ THE DOUBLE-CHECK, AND ONLY ON THE FREE-TYPED BRANCH (board 2186). Off the price book the
        payment method is a real choice — staff say "cash" and the passenger then pays by card — so
        the nurse states it deliberately rather than by leaving a default. On a price-book
        destination there is nothing to double-check: the home account is the only payer.
        ⭐ D3: NOTHING IS STORED. Its only value is the pause it forces, which is why the spec pins
        that it BLOCKS submit and not merely that it renders.
        ⭐ Board 2706: and only when Cash / card is the chosen payer (`payerConfirmShown`).
      */}
      {payerConfirmShown(values) && (
        <>
          <label class="checkbox-label" for="rf-payer-confirm">
            <input
              id="rf-payer-confirm"
              type="checkbox"
              {...errorProps('payerConfirmed')}
              checked={values.payerConfirmed}
              onChange={(e) =>
                setField('payerConfirmed', (e.target as HTMLInputElement).checked)
              }
            />
            {requestFormCopy.payerConfirmLabel}
          </label>
          {fieldError('payerConfirmed')}
        </>
      )}


      {/* ⭐⭐ P1-2 (T1717 M4, DD3) — THE EXPLICIT ROSTER UPDATE, AND THE ONLY WAY
          A REQUEST EVER CHANGES THE STANDING RECORD.
          ⛔ IT RENDERS ONLY WHEN A PREFILLED VALUE HAS ACTUALLY BEEN CHANGED, and
          it sits directly under the field it is about so the question arrives
          where the change was made rather than at the foot of the form.
          ⚠️ ONE FIELD SINCE T1868 M6 (the room); it was the last of three, and
          the placement RULE is what survives the reduction — a second prefilled
          field goes above this control, never below it.
          ⛔ AND IT IS GATED ON `residentsWritable`. Offering to update a roster
          the server will refuse to write is the same false promise P1-1 removed
          from the resident hint; the request itself is never gated, so nothing is
          lost by hiding the optional half.
          ⚠️ NO `aria-invalid` AND NO ERROR PARAGRAPH: a checkbox is always in a
          valid state, and this key never reaches the validator at all
          (`assembleRequestPayload` deliberately omits it). The refusal notice
          below is a NOTICE, not a field error. */}
      {residentsWritable && rosterDiffers(values) && (
        <>
          <label class="checkbox-label" for="rf-update-roster">
            <input
              id="rf-update-roster"
              type="checkbox"
              checked={values.updateRoster}
              onChange={(e) => setField('updateRoster', (e.target as HTMLInputElement).checked)}
            />
            {requestFormCopy.updateRosterLabel}
          </label>
          <p class="field-hint">{requestFormCopy.updateRosterHint}</p>
          {rosterUpdateNotice !== null && (
            <p class="notice" role="alert">
              {rosterUpdateNotice}
            </p>
          )}
        </>
      )}

      {/* ⭐⭐ T1868 M2 — ONE CONTROL FOR "DOES THE BUS BRING THEM BACK", NOT TWO.
          ⛔ THE FORM SAID IT TWICE. This radio pair drove `scheduleMode`
          (one-off / regular) and a SEPARATE checkbox below drove `returnTrip`
          ("The bus brings them back"). A nurse could pick "One-off trip" and tick
          the box, or pick "Regular trip" and tick it — two controls, one question,
          and the second one is the only one that ever reached a booking.
          ⇒ The pair now binds to `returnTrip` DIRECTLY. `scheduleMode` and the
          `ScheduleMode` type are deleted, the checkbox is gone, and its hint moved
          here so the explanation stays with the control that needs it.
          ⛔ `setReturnTrip`, NOT `setField`: untick must CLEAR the return pair, or
          a nurse who picks Return, fills the two boxes, then goes back to One way
          leaves a composed return instant on a journey that has none.
          ⭐ CONSEQUENCE WORTH STATING: `journeyDate` is now ALWAYS set, so the
          validator's one-off-XOR-pattern rule is satisfied by construction. */}
      <fieldset {...errorProps('returnTrip')}>
        <legend>{requestFormCopy.scheduleLegend}</legend>
        <label class="radio-label" for="rf-trip-oneway">
          <input
            id="rf-trip-oneway"
            type="radio"
            name="returnTrip"
            checked={!values.returnTrip}
            onChange={() => setReturnTrip(false)}
          />
          {requestFormCopy.oneOffOption}
        </label>
        <label class="radio-label" for="rf-trip-return">
          <input
            id="rf-trip-return"
            type="radio"
            name="returnTrip"
            checked={values.returnTrip}
            onChange={() => setReturnTrip(true)}
          />
          {requestFormCopy.weeklyOption}
        </label>
        <p class="field-hint">{requestFormCopy.returnTripHint}</p>
      </fieldset>
      {fieldError('returnTrip')}

      {/* ⛔ ONE NAMED GROUP FOR THE APPOINTMENT PAIR (2026-09-13, Alex, board
          2073). The "arrive or collect" radio used to name this pair by asking
          what the time meant; the form asks the appointment time only now, so
          the legend names it instead. Same `fieldset`/`legend` shape as the
          trip-type group above: no new class, no new component.
          ⛔ THE LEGEND CARRIES NEITHER "date" NOR "time" — the label floor in
          `test/client/request-form.spec.tsx` walks legends as well as labels,
          and the e2e `getByLabel('Date')` / `getByLabel('Time')` needles would
          otherwise match two elements. */}
      <fieldset>
          <legend>{requestFormCopy.appointmentLegend}</legend>
          <label class="field-label" for="rf-date">
            {requestFormCopy.dateLabel}
          </label>
          <input
            id="rf-date"
            {...errorPropsFirst('journeyDate', 'journey')}
            type="date"
            value={values.journeyDate}
            onInput={(e) => setAppointmentField('journeyDate', (e.target as HTMLInputElement).value)}
          />
          {/* ⛔⛔ EACH MESSAGE IMMEDIATELY AFTER ITS OWN CONTROL — T1717 M7,
              code-review. Both inputs used to render, and THEN both messages, in
              REVERSE order (time, then date). A nurse holding two errors read
              them against the wrong fields, and a screen reader announced two
              loose alerts after a pair of controls — which is precisely the
              defect the RETURN block's own comment describes and which M5 fixed
              there. The outbound pair had been carrying it since before M5.
              ⚠️ ABOVE the horizon hint, not below it: the error is the control's
              IMMEDIATE next sibling, which is the property the arm asserts and
              the one `aria-describedby` alone cannot give a sighted nurse. */}
          {fieldError('journeyDate')}
          {/* ⛔ T1868 M2 — `journey` RENDERS HERE, not adrift at the end of the
              section where it used to sit. Since the recurrence refusal returns
              first, this key means exactly one thing — "no date was given" — so
              its message belongs immediately after the date control, which is
              this file's own rule ("EACH MESSAGE IMMEDIATELY AFTER ITS OWN
              CONTROL"). Left where it was, it was a loose alert announced after a
              pair of controls, the very shape the comment below records fixing
              for the date/time pair. */}
          {fieldError('journey')}
          <p class="field-hint">{requestFormCopy.dateHorizonHint}</p>
          <label class="field-label" for="rf-time">
            {requestFormCopy.timeLabel}
          </label>
          <input
            id="rf-time"
            {...errorPropsFirst('journeyTime', 'pickupAt')}
            type="time"
            value={values.journeyTime}
            onInput={(e) => setAppointmentField('journeyTime', (e.target as HTMLInputElement).value)}
          />
          {fieldError('journeyTime')}
          {/* ⛔ `pickupAt` RENDERS UNDER THE TIME SHE TYPED (2026-09-13, Alex,
              board 2073). The pickup is derived from the home's offset and never
              shown, so the validator's refusals about it — the appointment is too
              soon for the bus to collect in time, or nothing could be derived —
              have no control of their own; the appointment time is the one she
              can change. `errorPropsFirst` above marks the control for whichever
              key fired; the validator gates the pickup block on the journey
              instant, so the two never fire together. */}
          {fieldError('pickupAt')}
          {/* ⭐⭐ THE DERIVED COLLECTION, READ-ONLY (board 2188, Alex 2026-09-18).
              It reverses R-2073-c, which removed exactly this from the nurse's
              card on the ground that she SHALL NOT see the guess.
              ⛔ NOT A CONTROL, and never becomes one: no input, no label `for`,
              no name. The nurse states the appointment; this is what follows
              from it and the home's own offset.
              ⛔ IT RECALCULATES BY BEING RE-READ, not by a subscription. Both
              appointment controls write through `setAppointmentField`, which
              re-derives the pair, so this line is correct on every keystroke
              simply because the component re-renders.
              ⚠️ Rendered ONLY when there is something to show — an empty preview
              means no offset for this home, a half-typed appointment, or an
              invalid instant, and each of those deserves silence rather than a
              placeholder. */}
          {/* ⚠️ NO NEW CLASS, deliberately. `style-contract.spec.ts`'s
              `UNDEFINED_ALLOWED` is EMPTY, so any class here would owe a rule in
              `app.css` — a file outside this piece's scope — and a check against
              the per-screen adoption census. `.field-hint` already exists, is
              census-untracked, and is what every other explanatory line on this
              form wears. */}
          {collectionPreview !== '' && (
            <p class="field-hint">
              {requestFormCopy.collectionDerivedLabel} {collectionPreview}.{' '}
              {requestFormCopy.collectionDerivedHint}
            </p>
          )}
      </fieldset>

      {/* ⭐⭐ T1683 M7 (A4) + T1717 M5 (DD4, P0-7) — THE ONLY SURFACE **THE
          NURSE** ANSWERS THIS ON, AND SINCE M5 IT ASKS **WHEN** AS WELL AS
          WHETHER.

          ⭐⭐ THE PLACEMENT RULING IS REVERSED, AND THIS BLOCK MOVED HERE
          BECAUSE OF IT. It used to render beside "Where to", on the argument
          that beside the date and time it reads as the opening of "…and back at
          what time?" — a question with no field behind it. It HAS a field now,
          so the pair belongs together: out at this time, back at that one, read
          as one schedule in one place. Alex, 2026-09-02, via `/scope` of the QA
          report (question 708 in the lane store); boards #1681 and #1683 M7
          record the reversal, and `copy.ts` carries the same paragraph rewritten
          rather than a second copy of it.
          ⚠️ AFTER THE WHOLE APPOINTMENT GROUP. (The meaning radio that used to
          sit between the time and this block is gone — 2026-09-13, Alex, board
          2073 — so the placement rule it justified is now simply "after the
          group".)
          ⚠️ ONE PLACEMENT SERVES BOTH MODES. The block follows the one-off
          date/time in one-off mode and the pattern block in recurring mode
          because it follows the branch itself — which is also why a recurring
          request cannot drift into having two placements to keep in step.

          ⛔⛔ A KIOSK CONTROL SURVIVES, AND AN OPERATOR CAN OVERRIDE THIS ANSWER.
          What M7 removed is the tick on Helen's portal-request inbox
          (`quote-helper/.../admin/PortalRequests.tsx`). The kiosk QUOTE screen
          still renders a live "Return trip" switch for a nursing job, and it is
          REGISTRY-DRIVEN — which is why grepping for the deleted `toggleReturn`
          returns nothing and reads as confirmation. It comes from the generic
          `<Requirements>` over the add-on registry (`shared/addons-schema.ts`,
          `engineKey: 'returnTrip'`, `validFor: 'all'`), and it REACHES THE
          COLUMN: `actions.ts` → `persist-quote.ts` → `bookings-repo.ts`
          (`UPDATE bookings SET … return_trip = $2`). Measured on real
          PostgreSQL through the production functions at the M7 security review:
          an ordinary quote save carrying `options.returnTrip:false` took a
          portal-set `bookings.return_trip` from true to false.
          ⚠️ THE INSTANT HAS NO KIOSK EDIT CONTROL, so it is not overridable in
          T1717 (DD6). Every render of it is gated on the boolean, so an operator
          flip to one-way hides the time consistently rather than showing a
          return time for a job that no longer comes back.

          ⭐ WHAT M7 DID CLOSE, STATED WITHOUT OVERSTATING IT. Under M1 the
          operator tick doubled the FIGURE and wrote NOTHING, so the money said
          return, the booking said one-way, and no row recorded the tick at all.
          Now the same tick writes, and the nurse's answer survives beside it:
          `inquiries.return_trip` is written ONCE at the crossing and never
          updated (`grep -rn "UPDATE inquiries SET" quote-helper/src` — status,
          customer_id and booking_id only), so nurse-said vs operator-quoted is a
          two-row comparison, and the kiosk badges the divergence "Edited" live
          against its immutable `customerFlags` baseline.

          ⛔ A HARD LOCK ON THE KIOSK SWITCH WAS CONSIDERED AND NOT TAKEN, and
          that is the owner's call rather than this file's: Helen quotes every
          booking by phone, so locking it would leave her unable to serve
          "actually, we need it to come back" at all. Pinned either way by the
          nursing-home arms in
          `quote-helper/test/client/manual-quote.spec.tsx`, which RED if the
          switch is ever locked or removed and send the reader back here.

          ⛔ NO `aria-invalid` AND NO ERROR PARAGRAPH **ON THE TICK**. A checkbox
          is always in a valid state from a browser; `errors['returnTrip']` needs
          a hand-built POST, and rendering a message that cannot appear is how a
          reader later concludes the field has a validation rule the UI enforces.
          The two inputs below are ordinary text-entry controls and carry both. */}
      {/* ⭐⭐ ONE BORDERED GROUP, TICK AND PAIR TOGETHER — T1717 M5, ux-review.
          ⛔ WHAT WAS WRONG. The two revealed controls rendered as two TOP-LEVEL
          fields — same `.field-label`, same vertical rhythm as "Where to" and the
          notes box — with nothing tying them to the tick that revealed them. The
          sibling "arrive or collect" question WAS a bordered `<fieldset>` then
          (the meaning radio and the pickup fieldset are gone, 2026-09-13, board
          2073; the trip-type pair above is the bordered sibling now), so the
          screen said, in its own visual language, that these were unrelated
          questions.
          ⚠️ NO NEW CLASS: `fieldset`/`legend` are styled at ELEMENT level in
          `app.css`, so the bordered treatment is what a plain fieldset already
          gets, and `style-contract.spec.ts`'s empty allow-list stays empty.
          ⚠️ THE LEGEND IS `visually-hidden` AND THAT IS THE WHOLE POINT OF IT:
          the group needs an accessible name for a screen reader, and the tick's
          own label — the first visible thing inside the border — is already the
          visible title. Rendering the same sentence twice would be clutter on the
          screen this client bounced off a competitor for having too much on. */}
      {/* ⛔ THE CHECKBOX IS GONE (T1868 M2), NOT HIDDEN. It asked the same question
          as the trip-type radios above — "does the bus bring them back" — and two
          controls for one question is what LCC asked us to remove. Its hint moved
          up to the fieldset that now owns the question. What survives here is the
          date/time pair, which still belongs below the outbound leg.
          ⛔ THE LEGEND IS NOT OPTIONAL. It used to be the tick's own phrasing, so
          deleting the tick left this group ANONYMOUS — two date/time controls with
          no accessible name, which is precisely the "two top-level fields" shape
          ux-review rejected at T1717 M5. Sighted users keep the border either way;
          only a screen-reader user loses by its absence. */}
      {/* ⛔ THE WHOLE GROUP IS CONDITIONAL, NOT JUST ITS CONTENTS (T1868 M2, review).
          Naming the fieldset fixed an anonymous group, but left it rendering EMPTY
          on the One way branch — a named group holding nothing, announced to a
          screen-reader user as a landmark with no content. Gating the fieldset
          itself means the group exists exactly when its fields do. */}
      {values.returnTrip && (
      <fieldset>
        <legend class="visually-hidden">{requestFormCopy.returnWhenLegend}</legend>

        {/* ⭐⭐ T1717 M5 (DD4) — WHEN THE BUS BRINGS THEM BACK.
            ⛔ RENDERED ONLY WHEN THE TRIP TYPE IS **Return**, and since T1868 M2
            that is the ONLY condition — the second half of this guard used to be
            "and the request is one-off", because a recurring request had no single
            outbound instant to come back from. There are no recurring requests now,
            so that clause was not simplified away, it became UNREACHABLE.
            Not hidden with CSS and not disabled: a control that is not on the
            screen cannot be filled in by accident.
            ⛔ BLANK BY DEFAULT — T632's rule, and here the consequence of breaking
            it is a bus sent at an hour nobody stated. `setReturnTrip` clears the
            time on an untick so "default" means every time it appears.
            ⛔ ONE CONTROL, SAME-DAY ONLY (2026-09-13, Alex, board 2073). The
            return date control T1717 M5 added is gone: the return is composed on
            the appointment's date and a multi-day return is by phone.
            ⛔ THE LABEL IS THE SITE'S OWN PROVEN ONE and contains neither "date"
            nor "time"; `test/client/request-form.spec.tsx` derives the live e2e
            `getByLabel` vocabulary and reds if it ever does.
            ⛔⛔ BOTH KEYS RENDER UNDER THIS ONE CONTROL. `returnTime` is the blank
            or malformed half; `returnAt` is the composed instant, still raised
            for a return not after the appointment (09:00 back on a 10:00
            appointment is the ordinary slip now) and for the window. With no
            date control left, dropping its render would give the nurse focus and
            no message. `errorPropsFirst` marks the input for whichever fired.
            ⭐⭐ T2088 M4 (ADR-0006, plan D3, R-1) — THE TIME MAY BE LEFT BLANK.
            A ticked return with this control empty is no longer refused: the
            assembler sends `returnAtState: 'to-confirm'` in place of an
            instant, the row reads "time to confirm", and Helen rings. The hint
            under the control is the whole of the affordance (D3 rejected a
            second checkbox: a new census entry and a new control the design
            board has not seen, for a state one sentence carries).
            ⛔ THE HINT SITS BELOW THE MESSAGES, NOT BETWEEN THE CONTROL AND
            THEM: this file's rule is that a message is its control's IMMEDIATE
            next sibling (`request-form.spec.tsx` asserts `nextElementSibling`
            for the `returnAt` message), and the date control's horizon hint
            takes the same position for the same reason.
            ⛔ `returnAtState` RENDERS HERE TOO, third: it is the marker's own
            key, unreachable from this form but reachable from a hand-built
            POST, and `ERROR_FIELD_IDS` focuses this control for it. */}
        <>
            <label class="field-label" for="rf-return-time">
              {requestFormCopy.returnTimeLabel}
            </label>
            <input
              id="rf-return-time"
              {...errorPropsFirstDescribedBy(RETURN_TIME_HINT_ID, 'returnTime', 'returnAt', 'returnAtState')}
              type="time"
              value={values.returnTime}
              onInput={(e) => setField('returnTime', (e.target as HTMLInputElement).value)}
            />
            {fieldError('returnTime')}
            {fieldError('returnAt')}
            {fieldError('returnAtState')}
            <p class="field-hint" id={RETURN_TIME_HINT_ID}>{requestFormCopy.returnTimeOptionalHint}</p>
        </>
      </fieldset>
      )}

      <label class="field-label" for="rf-notes">
        {requestFormCopy.notesLabel}
      </label>
      <textarea
        id="rf-notes"
            {...errorProps('notes')}
        maxLength={500}
        value={values.notes}
        onInput={(e) => setField('notes', (e.target as HTMLTextAreaElement).value)}
      />
      {fieldError('notes')}

      {submitError && (
        <p class="field-error" role="alert">
          {submitError}
        </p>
      )}
      <button type="submit" class="btn btn--primary" disabled={submitting || sendPending}>
        {submitting || sendPending ? requestFormCopy.submittingButton : requestFormCopy.submitButton}
      </button>
    </form>
  );
}
